import { useForm, yupResolver } from "@mantine/form"
import { useLocalStorage } from "@mantine/hooks"
import { CheckCircle, Paperclip, Trash } from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Checkbox,
  FormInput,
  FormQuerySelector,
  Group,
  Modal,
  SpinnerOverlay,
  Tag,
  Text,
  Toast,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import "@vesatogo/grass-dates/index.css"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { cloneDeep, debounce, get, isEmpty, omit, pick, set } from "lodash-es"
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useClient } from "urql"
import AttachmentModal from "~/components/AttachmentModal"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { FormCard } from "~/components/FormCard"
import {
  GstBreakdownCard,
  type GstAttachment,
} from "~/components/GstBreakdownCard"
import HowIsItTraded from "~/components/HowIsItTraded"
import { PackingMaterialSaveModal } from "~/components/PackingMaterial/PackingMaterialSaveModal"
import Payment from "~/components/Payment"
import QCObservations from "~/components/QCObservations"
import SendConsent from "~/components/SendConsent"
import TDSComponent from "~/components/TDSComponent"
import TermsAndConditions from "~/components/TermsAndConditions"
import { TradeParticulars } from "~/components/Trade/TradeParticulars"
import TradeExpenses from "~/components/TradeExpenses"
import WhatIsBeingTraded from "~/components/WhatIsBeingTraded"
import { WhoIsTrading } from "~/components/WhoIsTrading"
import WhoOverseesTrading from "~/components/WhoOverseesTrading"
import Codenames from "~/constants/Codenames"
import { TradeKinds, canAddAdvancePayment } from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import TradeStatus from "~/constants/TradeStatus"
import { AppRoutes } from "~/constants/routes"
import {
  BookingTradeSummaryQuery,
  DemandSellerDetailQuery,
  OfferBuyerDetailQuery,
  TradeDetailQuery,
  VehicleDetailDocument,
  useAddUpdateSharedEntityMutation,
  useAllDepartmentsQuery,
  useAllPackagingCommoditiesQuery,
  useAllSiUnitsQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useAllVehiclesQuery,
  useBookingTradeSummaryQuery,
  useBuyerCreditWalletInfoSummaryQuery,
  // useBuyerOverdueDetailsQuery,
  useDeleteBuyerGstAttachmentsMutation,
  useDeleteExpenseItemMutation,
  useDeleteSellerGstAttachmentsMutation,
  useDemandSellerDetailQuery,
  useGetSharedEntityQuery,
  useIncrementTradeCounterMutation,
  useOfferBuyerDetailQuery,
  useOfferOrderListQuery,
  useOrganisationPricingQuery,
  useSellerReceivableAmountQuery,
  useTradeDetailQuery,
  useUnitSiUnitsQuery,
  useUpdateTradeAttachmentsMutation,
  useUpsertTradeMutation,
  useUserPackingQuery,
} from "~/generated/graphql"
import { tradeValidationSchema } from "~/models/trade"
import { DeviationModalConfirmation } from "~/pages/d/credit/DeviationModalConfirmation"
import { OverdueModalConfirmation } from "~/pages/d/credit/OverdueModalConfirmation"
import { getBookingQuantity } from "~/utils/bookingPavtiUtils"
import {
  expenseAmount,
  expenseAmountBuyer,
  expenseAmountSeller,
  normalizeExpenses,
} from "~/utils/expenseAmount"
import {
  buildTradeCommodityItemsFromBackend,
  buildTradeCommodityItemsFromState,
  buildTradeItemInputsFromBlocks,
  createEmptyTradeCommodityBlock,
  getCommodityParticularsTotals,
  hasMultipleVarietiesWithGst,
  inferAllowMultipleCommodity,
  syncNestedTradeToFormValues,
  syncPackagingFlatToBlock,
} from "~/utils/tradeCommodityItems"
import { useDepartment } from "../../../utils/useDepartment"
import { SideBar } from "./sections/SideBar"

type SharedEntity = {
  group: SharedEntityGroup
}

const REGISTRATION_REDIRECT_FORM_STATE_KEY = "registration-redirect-form-state"

const REGISTRATION_FORM_DATE_FIELDS = [
  "start_date",
  "end_date",
  "expiry",
] as const

function persistRegistrationFormState(values: Record<string, unknown>) {
  try {
    localStorage.setItem(
      REGISTRATION_REDIRECT_FORM_STATE_KEY,
      JSON.stringify(values)
    )
  } catch (err) {
    console.error("Failed to persist form state for registration redirect", err)
  }
}

function parsePersistedRegistrationFormState(raw: string | null) {
  if (!raw || raw === "null") return null

  try {
    const restored = JSON.parse(raw)
    REGISTRATION_FORM_DATE_FIELDS.forEach(field => {
      if (restored[field]) restored[field] = new Date(restored[field])
    })
    return restored
  } catch (err) {
    console.error("Failed to parse persisted form state", err)
    return null
  }
}

function getVehicleIdFromRedirectUrl(searchParams: URLSearchParams) {
  const vehicleId =
    searchParams.get("vehicleId") ?? searchParams.get("vehicle_id")
  if (vehicleId) return vehicleId

  // FMS may append `?vehicleId=` even when the redirect URL already has query params,
  // producing URLs like `...?offerId=1?vehicleId=123` that searchParams cannot parse.
  const match = window.location.href.match(/[?&]vehicleId=([^&#]+)/i)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

const TradeRequestDetail = () => {
  const navigate = useNavigate()
  const client = useClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [attachmentModal, setAttachmentModal] = useState(false)
  const [, upsertSharedEntity] = useAddUpdateSharedEntityMutation()
  const [{ fetching: mutationFetching, data: uData }, upsertTrade] =
    useUpsertTradeMutation()

  const [{ fetching: updateTradeFetching }, updateTrade] =
    useUpdateTradeAttachmentsMutation()
  const [, deleteExpenseItem] = useDeleteExpenseItemMutation()
  const [, incrementTradeCounter] = useIncrementTradeCounterMutation()
  const [, deleteSellerGstAttachments] = useDeleteSellerGstAttachmentsMutation()
  const [, deleteBuyerGstAttachments] = useDeleteBuyerGstAttachmentsMutation()
  const { id: sharedDeptId, department: departmentData } = useDepartment()
  const [dept] = useLocalStorage({ key: "department" })
  const hasRestoredState = useRef(false)
  const hasAppliedRedirectState = useRef(false)
  const expenseGstManualNoticeShown = useRef(false)
  const canViewVehicleSelection = useAuthorization(
    Permission.canViewVehiclesUnderTrade
  )

  const canAddAttachmentsAfterTradeVerified = useAuthorization(
    Permission.CanAddAttachmentsAfterTradeVerified
  )

  const canAddMultiCommodity = useAuthorization(
    Permission.CanAddMultiCommodityTradesAndReceipts
  )

  const [{ data: alldepts }] = useAllDepartmentsQuery()

  const currentDept = alldepts?.department?.find(
    dep => dep?.id?.toString() === dept?.toString()
  )

  const [{ data: deptData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteDepartment,
      codename: currentDept?.id,
    },
    pause: !currentDept?.id,
  })

  const useCredit = useMemo(() => {
    return deptData?.setu_trade_shared_entity?.[0]?.department_config
      ?.use_credit
  }, [deptData])

  const [{ data: sData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteCurrency,
      codename: Codenames.KiteDefaultCurrency,
    },
  })
  const [packingOpen, setPackingOpen] = useState(false)
  const [isNavigateOn, setNavigate] = useState(false)

  const defaultCurrency = sData?.setu_trade_shared_entity?.[0]
  const { id } = useParams()
  const type = searchParams.get("type")
  const offerId = searchParams.get("offerId")
  const demandId = searchParams.get("demandId")
  const buyerId = searchParams.get("buyerId")
  const sellerId = searchParams.get("sellerId")
  const createOrder = (type as string) === "order"
  const materialKind = searchParams.get("material_kind")

  const newHishobPatti = Boolean(searchParams.get("new"))
  const newInwardPatti = Boolean(searchParams.get("newInward"))
  const newStoragePatti = Boolean(searchParams.get("newStorage"))
  const newBookingPatti = Boolean(searchParams.get("newBooking"))

  const form = useForm<any>({
    initialValues: {
      mode: "direct",
      kind: createOrder ? TradeKinds.OrderPatti : TradeKinds.SaudaPavti,
      attachments: [] as any,
      packaging: "raw_material",
      material_kind: materialKind,
      trade_items: [{ rate: 0 }],
      trade_commodity_items: [createEmptyTradeCommodityBlock()],
      allow_multiple_commodity: false,
      start_date: new Date(),
      end_date: null,
      expiry: null,
      expenses: [{ buyer_expense: {} }],
      advance_payment: [],
      credit_notes: [],
      tracking_code: crypto.randomUUID(),
      supplier_tds_value: false,
      // GST
      supplier_gst_value: 0,
      buyer_gst_value: 0,
      supplier_gst_auto_calculated: true,
      buyer_gst_auto_calculated: true,
      expense_supplier_gst_auto_calculated: true,
      expense_buyer_gst_auto_calculated: true,
      seller_gst_attachments: [] as any,
      buyer_gst_attachments: [] as any,
      expense_seller_gst_attachments: [] as any,
      expense_buyer_gst_attachments: [] as any,
      is_intercompany_transaction: "no",
    },
    validate: yupResolver(tradeValidationSchema),
  })

  const qcForm = useForm<any>({
    initialValues: {
      results: [],
    },
  })

  const creditForm = useForm<any>({
    initialValues: { credit: {} },
  })
  const { values: state, setValues: setState } = form

  const createSauda =
    ((type as string) === TradeKinds.SaudaPavti ||
      state.kind === TradeKinds.SaudaPavti) &&
    (demandId || state?.demand_id)

  const [isOpen, setOpen] = useState(false)
  const [isDeviation, setDeviation] = useState(false)
  const [isOverdue, setOverdue] = useState(false)
  const [showExpenseGstManualNotice, setShowExpenseGstManualNotice] =
    useState(false)

  const fetchingGradeRef = useRef(new Set<number>())

  const idNum = parseInt(id as any)

  const [removedTradeItems, setRemovedTradeItems] = useState<
    Record<string, any>
  >({})
  const [removedExpenseItems, setRemovedExpenseItems] = useState<
    Record<string, any>
  >({})
  const [{ data: dData, fetching }] = useTradeDetailQuery({
    variables: { id: idNum },
    pause: !idNum,
    requestPolicy: "network-only",
  })

  const [{ data: receivableData, fetching: sellerFetching }] =
    useSellerReceivableAmountQuery({
      variables: { id: dData?.trade_by_pk?.seller?.id },
      pause: !dData?.trade_by_pk?.seller?.id,
    })

  const [{ data: offerData, fetching: offerFetching }] =
    useOfferBuyerDetailQuery({
      variables: {
        id: offerId,
        buyerId: buyerId,
      },
      pause: !offerId || !buyerId,
    })
  const [{ data: demandData, fetching: demandFetching }] =
    useDemandSellerDetailQuery({
      variables: {
        id: demandId,
        sellerId: sellerId,
      },
      pause: !demandId || !sellerId,
    })

  const [{ data: UnitSI }] = useUnitSiUnitsQuery()

  const isPackaging =
    materialKind === "packaging" ||
    dData?.trade_by_pk?.material_kind === "packaging" ||
    offerData?.offers_by_pk?.material_kind === "packaging" ||
    demandData?.demand_by_pk?.material_kind === "packaging"

  const [{ data: pData }] = useUserPackingQuery({
    variables: {
      where: {
        user_id: {
          _eq: state?.seller?.shared_id,
        },
      },
    },
  })

  const [{ data: creditData, error, fetching: creditFetching }] =
    useBuyerCreditWalletInfoSummaryQuery({
      variables: {
        userId: state?.buyer?.shared_id,
        tradeId: id !== "new" ? id : undefined,
        deptId: sharedDeptId,
      },
      pause: !state?.buyer?.shared_id || !useCredit,
      requestPolicy: "network-only",
    })

  const [{ data: offerOrderData }] = useOfferOrderListQuery({
    variables: {
      where: {
        variety_grade_id: {
          _eq: dData?.trade_by_pk?.trade_items?.[0]?.variety_grade?.id,
        },
        trades: {
          kind: {
            _eq: TradeKinds.OrderPatti,
          },
        },
        // offer_storage_pickeds: {
        //   offer_order_pickeds: {
        //     trade: { deducted_quantity_remaining: { _gt: 0.0 } },
        //   },
        // },
      },
    },
    pause: !dData?.trade_by_pk?.offer_id,
  })

  // Auto select grade for packaging material type
  const [{ data: packagingGrades }] = useAllVarietyGradesQuery({
    variables: { variety: state?.commodity_variety?.id },
    pause: !isPackaging || !state?.commodity_variety?.id,
  })

  useEffect(() => {
    creditForm?.setValues(val => ({
      credit: creditData?.buyer_credit_wallet_info
        ?.latest_verified_credit_wallet_request_id
        ? creditData?.buyer_credit_wallet_info
        : error
        ? { error: error?.message }
        : null,
    }))
  }, [creditData, error])

  const credit = creditForm?.values?.credit

  // Disable the form is the trade has a receipt associated with it because it will create mismatch in receipt calculations.
  const hasReceipt = dData?.trade_by_pk?.receipt_id
  const hasInvoice = dData?.trade_by_pk?.invoice !== null

  const getRemainingQty = (item?: any) => {
    return createSauda
      ? Number(demandData?.demand_by_pk?.quantity) -
          Number(
            demandData?.demand_by_pk?.trade_qty_used?.aggregate?.sum
              ?.total_quantity
          )
      : offerId
      ? Number(offerData?.offers_by_pk?.quantity) -
        Number(
          offerData?.offer_order_picked_aggregate?.aggregate?.sum?.quantity || 0
        )
      : getBookingQuantity(bookingData, item?.variety_grade?.shared_id)
  }

  function updateTradeState(
    trade?: TradeDetailQuery["trade_by_pk"],
    bookingGrades?: BookingTradeSummaryQuery,
    offer?: OfferBuyerDetailQuery["offers_by_pk"],
    demand?: DemandSellerDetailQuery["demand_by_pk"]
  ) {
    if (!trade && !offer?.id && !demand?.id) return

    const creatingNewChild = Boolean(
      newHishobPatti || newInwardPatti || newStoragePatti || newBookingPatti
    )

    const newState = {
      ...trade,
      // ...offer,
      attachments:
        trade?.attachments?.map(attachment => attachment) ||
        offer?.attachments?.map(attachment => attachment) ||
        demand?.attachments?.map(attachment => attachment),
      is_intercompany_transaction:
        trade?.is_intercompany_transaction === true
          ? "yes"
          : trade?.is_intercompany_transaction === false
          ? "no"
          : newHishobPatti ||
            newStoragePatti ||
            newInwardPatti ||
            newBookingPatti
          ? dData?.trade_by_pk?.is_intercompany_transaction === true
            ? "yes"
            : dData?.trade_by_pk?.is_intercompany_transaction === false
            ? "no"
            : undefined
          : undefined,
      parent_trade:
        ((dData?.trade_by_pk?.kind === TradeKinds.InwardPatti ||
          dData?.trade_by_pk?.kind === TradeKinds.StoragePatti ||
          dData?.trade_by_pk?.kind === TradeKinds.SaudaPavti ||
          dData?.trade_by_pk?.kind === TradeKinds.Booking) &&
          (newHishobPatti || newStoragePatti || newInwardPatti)) ||
        newBookingPatti
          ? pick(
              dData?.trade_by_pk,
              "id",
              "kind",
              "start_date",
              "is_multiple_commodity",
              "is_intercompany_transaction"
            )
          : // : dData?.trade_by_pk?.kind === TradeKinds.Booking && newHishobPatti
            // ? null
            trade?.parent_trade,
      material_kind:
        materialKind ||
        trade?.material_kind ||
        offer?.material_kind ||
        demand?.material_kind,
      kind: newHishobPatti
        ? TradeKinds.HishobPatti
        : newInwardPatti
        ? TradeKinds.InwardPatti
        : newBookingPatti
        ? TradeKinds.Booking
        : newStoragePatti
        ? TradeKinds.StoragePatti
        : createOrder
        ? TradeKinds.OrderPatti
        : createSauda
        ? TradeKinds.SaudaPavti
        : trade?.kind,
      invoice: trade?.invoice,
      buyer: trade
        ? trade?.buyer?.id
          ? { ...trade?.buyer?.user[0], shared_id: trade?.buyer?.id }
          : undefined
        : createOrder
        ? {
            ...offer?.enquiries?.[0]?.buyer?.user?.[0],
            shared_id: offer?.enquiries?.[0]?.buyer?.id,
          }
        : createSauda
        ? {
            ...demand?.buyer?.user?.[0],
            shared_id: demand?.buyer?.id,
          }
        : undefined,
      commodity: trade
        ? {
            ...trade.commodity?.commodity?.[0],
            shared_id: trade.commodity?.id,
          }
        : createOrder
        ? {
            ...offer?.commodity?.commodity?.[0],
            shared_id: offer?.commodity?.id,
          }
        : createSauda
        ? {
            ...demand?.commodity?.commodity?.[0],
            shared_id: demand?.commodity?.id,
          }
        : undefined,
      commodity_variety: trade
        ? {
            ...trade.commodity_variety?.commodity_variety?.[0],
            shared_id: trade.commodity_variety?.id,
          }
        : createOrder
        ? {
            ...offer?.commodity_variety?.commodity_variety?.[0],
            shared_id: offer?.commodity_variety?.id,
          }
        : createSauda
        ? {
            ...demand?.commodity_variety?.commodity_variety?.[0],
            shared_id: demand?.commodity_variety?.id,
          }
        : undefined,
      vehicle: trade?.vehicle
        ? {
            ...trade?.vehicle,
            shared_id: trade?.vehicle?.id,
          }
        : undefined,
      plot_crop: trade?.plot_crop?.plot_crop?.[0]
        ? {
            ...trade?.plot_crop?.plot_crop?.[0],
            shared_id: trade?.plot_crop?.id,
          }
        : undefined,
      facilitator: trade
        ? {
            ...trade?.facilitator?.facilitator?.[0],
            shared_id: trade?.facilitator?.id,
          }
        : createOrder
        ? {
            ...offer?.facilitator?.facilitator?.[0],
            shared_id: offer?.facilitator?.id,
          }
        : createSauda
        ? {
            ...demand?.facilitator?.facilitator?.[0],
            shared_id: demand?.facilitator?.id,
          }
        : undefined,
      mode: trade
        ? trade?.mode
        : createOrder
        ? offer?.mode
        : createSauda
        ? demand?.mode
        : undefined,
      seller: trade
        ? {
            ...trade?.seller?.user[0],
            shared_id: trade?.seller?.id,
            seller_receivable_amount_current_year:
              receivableData?.setu_trade_shared_entity_by_pk
                ?.seller_receivable_amount_current_year,
          }
        : createOrder
        ? {
            ...offer?.seller?.user[0],
            shared_id: offer?.seller?.id,
          }
        : createSauda
        ? {
            ...demand?.enquiries?.[0]?.seller?.user?.[0],
            shared_id: demand?.enquiries?.[0]?.seller?.id,
          }
        : undefined,

      start_date:
        (trade?.start_date && new Date(trade.start_date)) || new Date(),
      end_date: trade?.end_date && new Date(trade.end_date),
      expiry: trade?.expiry && new Date(trade.expiry),
      quantity_unit:
        isPackaging && !createOrder && !createSauda
          ? trade?.trade_items?.[0]?.quantity_unit?.id
            ? {
                ...trade.trade_items[0]?.quantity_unit?.si_unit?.[0],
                shared_id: trade.trade_items[0]?.quantity_unit?.id,
              }
            : UnitSI?.si_unit?.[0]
          : trade
          ? {
              ...trade.trade_items[0]?.quantity_unit?.si_unit?.[0],
              shared_id: trade.trade_items[0]?.quantity_unit?.id,
            }
          : createOrder
          ? {
              ...offer?.quantity_unit?.si_unit?.[0],
              shared_id: offer?.quantity_unit?.id,
            }
          : createSauda
          ? {
              ...demand?.quantity_unit?.si_unit?.[0],
              shared_id: demand?.quantity_unit?.id,
            }
          : undefined,
      trade_items: trade
        ? trade.trade_items?.length
          ? trade.trade_items.map(item => ({
              ...item,
              quantity:
                newBookingPatti ||
                newInwardPatti ||
                newHishobPatti ||
                newStoragePatti
                  ? getBookingQuantity(bookingGrades, item.variety_grade?.id)
                  : item.quantity,
              variety_grade: {
                ...item.variety_grade?.variety_grade[0],
                shared_id: item.variety_grade?.id,
              },
            }))
          : [{}]
        : createOrder
        ? [
            {
              rate: offer?.max_rate || offer?.rate,
              quantity: getRemainingQty(),
              variety_grade: {
                ...offer?.variety_grade?.variety_grade?.[0],
                shared_id: offer?.variety_grade?.id,
              },
            },
          ]
        : createSauda
        ? [
            {
              rate: demand?.max_rate || demand?.rate,
              quantity: getRemainingQty(),
              variety_grade: {
                ...demand?.variety_grade?.variety_grade?.[0],
                shared_id: demand?.variety_grade?.id,
              },
            },
          ]
        : undefined,
      expenses: trade
        ? normalizeExpenses(trade.expenses)
        : createOrder
        ? normalizeExpenses(offer?.expenses || [])
        : createSauda
        ? normalizeExpenses(demand?.expenses || [])
        : undefined,
      advance_payment: creatingNewChild
        ? []
        : trade?.advance_payments?.map(advance => ({
            ...advance,
            bourn_by: { id: advance.bourn_by, name: advance.bourn_by },
          })),
      credit_notes: trade?.credit_notes,
      amount_deducted: trade
        ? (trade?.total_amount * trade?.standard_deduction) / 100
        : createOrder
        ? (offer?.total_amount * offer?.standard_deduction) / 100
        : (demand?.total_amount * demand?.standard_deduction) / 100,
      quantity_deducted: trade
        ? (trade?.total_quantity * trade?.standard_deduction) / 100
        : createOrder
        ? (offer?.quantity * offer?.standard_deduction) / 100
        : createSauda
        ? (demand?.quantity * demand?.standard_deduction) / 100
        : undefined,

      officer: trade
        ? trade.officer?.user[0]
          ? { ...trade.officer?.user[0], shared_id: trade.officer?.id }
          : trade?.parent_trade
          ? trade?.parent_trade?.officer?.user[0]
            ? {
                ...trade?.parent_trade?.officer?.user[0],
                shared_id: trade?.parent_trade?.officer?.id,
              }
            : undefined
          : undefined
        : offer?.sr_officer?.user[0]
        ? { ...offer?.sr_officer?.user[0], shared_id: offer?.sr_officer?.id }
        : demand?.sr_officer?.user[0]
        ? { ...demand?.sr_officer?.user[0], shared_id: demand?.sr_officer?.id }
        : undefined,

      junior_officer: trade
        ? trade.junior_officer?.user[0]
          ? {
              ...trade.junior_officer?.user[0],
              shared_id: trade.junior_officer?.id,
            }
          : trade?.parent_trade
          ? trade?.parent_trade?.junior_officer?.user[0]
            ? {
                ...trade?.parent_trade?.junior_officer?.user[0],
                shared_id: trade?.parent_trade?.junior_officer?.id,
              }
            : undefined
          : undefined
        : offer?.jr_officer?.user[0]
        ? { ...offer?.jr_officer?.user[0], shared_id: offer?.jr_officer?.id }
        : demand?.jr_officer?.user[0]
        ? { ...demand?.jr_officer?.user[0], shared_id: demand?.jr_officer?.id }
        : undefined,
      demandId: demandData?.demand_by_pk?.id,
      offerId: offerData?.offers_by_pk?.id,
      supplier_tds_value: (() => {
        if (trade?.seller_tds !== null && trade?.seller_tds !== undefined) {
          return true
        }
      })(),
      // supplier_gst_auto_calculated:
      //   trade?.supplier_gst_auto_calculated !== undefined
      //     ? trade?.supplier_gst_auto_calculated
      //     : true,
      // buyer_gst_auto_calculated:
      //   trade?.buyer_gst_auto_calculated !== undefined
      //     ? trade?.buyer_gst_auto_calculated
      //     : true,
      expense_supplier_gst_auto_calculated:
        trade?.supplier_expense_gst_auto_calculated !== undefined
          ? trade?.supplier_expense_gst_auto_calculated
          : true,
      expense_buyer_gst_auto_calculated:
        trade?.buyer_expense_gst_auto_calculated !== undefined
          ? trade?.buyer_expense_gst_auto_calculated
          : true,
      seller_gst_attachments: trade?.seller_gst_attachments || [],
      buyer_gst_attachments: trade?.buyer_gst_attachments || [],
      expense_seller_gst_attachments:
        (trade as any)?.expense_seller_gst_attachments || [],
      expense_buyer_gst_attachments:
        (trade as any)?.expense_buyer_gst_attachments || [],
      expense_supplier_sgst: (trade as any)?.expense_supplier_sgst || 0,
      expense_supplier_cgst: (trade as any)?.expense_supplier_cgst || 0,
      expense_supplier_igst: (trade as any)?.expense_supplier_igst || 0,
      expense_buyer_sgst: (trade as any)?.expense_buyer_sgst || 0,
      expense_buyer_cgst: (trade as any)?.expense_buyer_cgst || 0,
      expense_buyer_igst: (trade as any)?.expense_buyer_igst || 0,
      expense_supplier_sgst_percentage:
        (trade as any)?.expense_supplier_sgst_percentage || 0,
      expense_supplier_cgst_percentage:
        (trade as any)?.expense_supplier_cgst_percentage || 0,
      expense_supplier_igst_percentage:
        (trade as any)?.expense_supplier_igst_percentage || 0,
      expense_buyer_sgst_percentage:
        (trade as any)?.expense_buyer_sgst_percentage || 0,
      expense_buyer_cgst_percentage:
        (trade as any)?.expense_buyer_cgst_percentage || 0,
      expense_buyer_igst_percentage:
        (trade as any)?.expense_buyer_igst_percentage || 0,
      ...((trade as any)?.taxes?.length > 0 &&
        (() => {
          const gst: Record<string, number> = {
            supplier_sgst: 0,
            supplier_cgst: 0,
            supplier_igst: 0,
            buyer_sgst: 0,
            buyer_cgst: 0,
            buyer_igst: 0,
            supplier_sgst_percentage: 0,
            supplier_cgst_percentage: 0,
            supplier_igst_percentage: 0,
            buyer_sgst_percentage: 0,
            buyer_cgst_percentage: 0,
            buyer_igst_percentage: 0,
          }
          for (const t of (trade as any).taxes) {
            const role = (t.role || "").toLowerCase()
            const name = (t.tax_name || "").toLowerCase()
            const key = `${role === "seller" ? "supplier" : "buyer"}_${name}`
            if (key in gst) gst[key] = t.amount ?? 0
            const pctKey = `${key}_percentage`
            if (pctKey in gst) gst[pctKey] = t.percentage ?? 0
          }
          return { ...gst, _gstFromBackend: !creatingNewChild }
        })()),
      ...((trade as any)?.expense_taxes?.length > 0 &&
        (() => {
          const expenseGst: Record<string, number> = {
            expense_supplier_sgst: 0,
            expense_supplier_cgst: 0,
            expense_supplier_igst: 0,
            expense_buyer_sgst: 0,
            expense_buyer_cgst: 0,
            expense_buyer_igst: 0,
            expense_supplier_sgst_percentage: 0,
            expense_supplier_cgst_percentage: 0,
            expense_supplier_igst_percentage: 0,
            expense_buyer_sgst_percentage: 0,
            expense_buyer_cgst_percentage: 0,
            expense_buyer_igst_percentage: 0,
          }
          for (const t of (trade as any).expense_taxes) {
            const role = (t.role || "").toLowerCase()
            const name = (t.tax_name || "").toLowerCase()
            const key = `expense_${
              role === "seller" ? "supplier" : "buyer"
            }_${name}`
            if (key in expenseGst) expenseGst[key] = t.amount ?? 0
            const pctKey = `${key}_percentage`
            if (pctKey in expenseGst) expenseGst[pctKey] = t.percentage ?? 0
          }
          return expenseGst
        })()),
    }

    const backendBlocks = trade
      ? buildTradeCommodityItemsFromBackend(
          newState.trade_items || [],
          {
            commodity: newState.commodity,
            commodity_variety: newState.commodity_variety,
            plot_crop: newState.plot_crop,
            quantity_unit: newState.quantity_unit,
            taxes: (trade as any)?.taxes,
            supplier_gst_auto_calculated: (trade as any)
              ?.supplier_gst_auto_calculated,
            buyer_gst_auto_calculated: (trade as any)
              ?.buyer_gst_auto_calculated,
            seller_gst_attachments: trade?.seller_gst_attachments,
            buyer_gst_attachments: trade?.buyer_gst_attachments,
          },
          { markGstFromBackend: !creatingNewChild }
        )
      : null

    const initBlocks =
      backendBlocks ?? buildTradeCommodityItemsFromState(newState)

    if (initBlocks?.[0]) {
      initBlocks[0].gst = {
        ...initBlocks[0].gst,
        seller_gst_attachments: trade?.seller_gst_attachments || [],
        buyer_gst_attachments: trade?.buyer_gst_attachments || [],
      }
    }

    const initialized = syncNestedTradeToFormValues({
      ...newState,
      allow_multiple_commodity:
        trade?.is_multiple_commodity !== undefined
          ? trade?.is_multiple_commodity
          : inferAllowMultipleCommodity({
              ...newState,
              trade_commodity_items: initBlocks,
            }),
      trade_commodity_items: initBlocks,
    })

    setState(initialized)
    runComputations(initialized)

    qcForm.setFieldValue(
      "results",
      trade?.trade_items
        ?.filter(item => item.result?.result?.[0])
        ?.map(item => {
          const result = item.result?.result?.[0]
          return {
            ...result,
            observations: result?.observations.map((obs, index) => ({
              ...omit(obs, ["sample_observation", "template_parameter"]),
              sample_observation_id: obs.sample_observation?.id,
              observation_number: obs.sample_observation?.observation_number,
              quality_parameter_id:
                obs.template_parameter?.quality_parameter_id,
              attachments: obs.sample_observation?.attachments?.map(
                attachment => attachment.media
              ),
              index,
            })),
            shared_id: item.result?.id,
          }
        })
    )
  }

  const deviationRequested = state?.status === TradeStatus.DEVIATION_REQUESTED
  const deviationApproved = state?.status === TradeStatus.DEVIATION_APPROVED
  const deviationRejected = state?.status === TradeStatus.DEVIATION_REJECTED
  const overdueApproved = state?.status === TradeStatus.OVERDUE_APPROVED
  const overdueRequested = state?.status === TradeStatus.OVERDUE_REQUESTED
  const overdueFinalApproval =
    state?.status === TradeStatus.OVERDUE_FINAL_APPROVAL
  const overdueRecommended = state?.status === TradeStatus.OVERDUE_RECOMMENDED
  const overdueRejected = state?.status === TradeStatus.OVERDUE_REJECTED

  const deviationRecommended =
    state?.status === TradeStatus.DEVIATION_RECOMMENDATION

  const tradeId = dData?.trade_by_pk?.id
  const isBookingTrade = state?.kind === TradeKinds.Booking

  const tradeOldKind = dData?.trade_by_pk?.kind

  const isTradeConfirmed = state?.status != "Draft"

  const inwardFromDemand =
    ((Boolean(searchParams.get("newInward")) ||
      state.kind === TradeKinds.InwardPatti) &&
      state?.demand_id) ||
    state?.parent?.demand_id

  const disableOfficerField =
    newBookingPatti ||
    newHishobPatti ||
    newInwardPatti ||
    newStoragePatti ||
    dData?.trade_by_pk?.parent_trade?.id

  const isParentBookingTrade =
    state?.parent_trade?.kind === TradeKinds.Booking ||
    (tradeOldKind === TradeKinds.Booking && newHishobPatti)

  const isParentSaudaPavti =
    state?.parent_trade?.kind === TradeKinds.SaudaPavti ||
    (tradeOldKind === TradeKinds.SaudaPavti && newHishobPatti)

  const parentTradeId = state?.parent_trade?.id

  const isChildPatti = Boolean(state?.parent_trade?.id)

  const isHishobPatti = isTradeConfirmed && newHishobPatti
  const isStoragePatti = state.kind === TradeKinds.StoragePatti
  const isOrderPatti = state.kind === TradeKinds.OrderPatti
  const isInwardPatti = newInwardPatti

  const isInheritedHishobPatti =
    (state?.parent_trade || newHishobPatti) &&
    state?.kind === TradeKinds.HishobPatti

  const isNewStorageHishobPatti =
    state?.parent_trade?.kind === TradeKinds.StoragePatti && newHishobPatti

  const noCreditAvailableHpBp =
    (isBookingTrade || state?.kind === TradeKinds.HishobPatti) &&
    state?.buyer?.id &&
    !creditForm?.values?.credit?.latest_verified_credit_wallet_request_id

  const canCreateTrade = !useCredit
    ? true
    : state?.parent_trade?.kind === TradeKinds.Booking && TradeKinds.HishobPatti
    ? credit?.dynamic_available >= state?.buyer_amount
      ? true
      : false
    : isBookingTrade || state?.kind === TradeKinds.HishobPatti
    ? credit?.dynamic_available >= state?.buyer_amount
    : true

  const disableUserCreditUse =
    (newBookingPatti ||
      newHishobPatti ||
      isInheritedHishobPatti ||
      isBookingTrade ||
      state.kind === TradeKinds.HishobPatti) &&
    Boolean(creditData?.buyer_credit_wallet_info?.requested_overdraft_count)

  const showDeviationInDraftWarning =
    (state.kind === TradeKinds.HishobPatti || isBookingTrade) &&
    Boolean(creditData?.buyer_credit_wallet_info?.requested_overdraft_count) &&
    state.status === "Draft"

  const isOverdueCheckTradeKind =
    isInheritedHishobPatti ||
    isBookingTrade ||
    newHishobPatti ||
    newBookingPatti ||
    state?.kind === TradeKinds.HishobPatti ||
    state?.kind === TradeKinds.Booking

  const isBuyerOverdue = false
  // creditData?.buyer_credit_wallet_info?.credit_wallet_id &&
  // creditData?.buyer_credit_wallet_info?.overdue_amount &&
  // creditData?.buyer_credit_wallet_info?.overdue_amount > 0 &&
  // isOverdueCheckTradeKind

  const disableOverdueApproval =
    isBuyerOverdue &&
    Boolean(
      creditData?.buyer_credit_wallet_info?.requested_overdue_approval_count
    )

  const canCreateNewOverdueApproval = isBuyerOverdue

  const isInvoiceCancelled = state?.status === Codenames.InvoiceCancelled
  const isCompletelyDisabled =
    (state?.kind === TradeKinds.InwardPatti &&
      state?.status === Codenames.DirectSale) ||
    hasReceipt ||
    (isTradeConfirmed &&
      id !== "new" &&
      !newHishobPatti &&
      !newInwardPatti &&
      !newStoragePatti &&
      !newBookingPatti) ||
    (useCredit && noCreditAvailableHpBp) ||
    deviationRejected ||
    deviationApproved ||
    deviationRequested ||
    deviationRecommended ||
    disableUserCreditUse ||
    isInvoiceCancelled ||
    overdueApproved ||
    overdueFinalApproval ||
    overdueRecommended ||
    overdueRejected ||
    overdueRequested ||
    disableOverdueApproval

  // Multi-commodity trades with GST-configured varieties: expense GST
  // cannot be derived from a single variety and must be entered manually.
  const expenseGstManualOnly = hasMultipleVarietiesWithGst(state)

  // Only on a brand-new parent trade being created (not an existing trade or
  // a child patti) do we force manual expense GST entry for multi-commodity.
  const expenseGstManualOnlyOnCreate =
    Number.isNaN(idNum) && !isChildPatti && expenseGstManualOnly

  const deviation = !isCompletelyDisabled && !canCreateTrade
  const isStatusGenerateSale = state?.status === Codenames.GenerateSales
  const isStatusInvoiced = state?.status === Codenames.Invoiced
  const isStatusGenerateInward = state?.status === Codenames.GenerateInward
  const isStatusInwardConfirmed = state?.status === Codenames.InwardConfirm
  const isStatusDraft = state?.status === Codenames.Draft

  const bookingSummaryId =
    state?.kind === TradeKinds.InwardPatti &&
    (newHishobPatti || newStoragePatti)
      ? tradeId
      : parentTradeId
      ? parentTradeId
      : dData?.trade_by_pk?.trades?.length ||
        newHishobPatti ||
        newInwardPatti ||
        newStoragePatti ||
        newBookingPatti ||
        isOrderPatti
      ? tradeId
      : undefined

  const total_returned =
    dData?.trade_by_pk?.trade_items_aggregate?.aggregate?.sum?.returned_quantity

  useEffect(() => {
    if (disableUserCreditUse && !dData?.trade_by_pk?.over_draft?.id) {
      toast.custom(
        <Toast
          title="Please settle all Deviations before proceeding!"
          intent="danger"
        />,
        { duration: 3000 }
      )
    }
  }, [disableUserCreditUse])

  const [{ data: bookingData }] = useBookingTradeSummaryQuery({
    variables: {
      id: bookingSummaryId,
    },
    pause: !bookingSummaryId,
    requestPolicy: "network-only",
  })

  const moreTrades = bookingSummaryId
    ? bookingData?.trade_grade_quantity_summary_report?.more_trade
    : true

  useEffect(() => {
    if (uData?.add_update_trade?.trade?.id) {
      setRemovedTradeItems({})
      setRemovedExpenseItems({})
    }
  }, [uData])

  useEffect(() => {
    if (isPackaging && UnitSI?.si_unit?.[0]) {
      const unit = UnitSI.si_unit[0]
      upsertSharedEntity({
        reference_id: unit.codename,
        group_name: SharedEntityGroup.KiteSiUnit,
        name: unit.name,
      }).then(({ data }) => {
        setState(prev => ({
          ...prev,
          quantity_unit: {
            ...unit,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
          },
        }))
      })
    }
  }, [isPackaging, UnitSI])

  useEffect(() => {
    if (
      dData &&
      currentDept &&
      dData?.trade_by_pk?.department.reference_id !== currentDept?.id
    ) {
      navigate("/d/trade-requests")
    }
  }, [currentDept, dData?.trade_by_pk?.department.reference_id])

  const [{ data: oData }] = useOrganisationPricingQuery()

  const runComputations = useMemo(
    () =>
      debounce(values => {
        const stateCopy = cloneDeep(values)
        const trade = stateCopy
        let tradeTotalQty = 0,
          tradeTotalAmount = 0,
          totalExpenseAmount = 0,
          buyerExpenseAmount = 0,
          supplierExpenseAmount = 0,
          advanceTotalAmount = 0
        for (let j = 0; j < trade.trade_items.length; j++) {
          let itemTotalAmount = 0
          const item = trade.trade_items[j]
          const quantity = Number(item.quantity) || 0
          const rate = Number(item.rate) || 0
          itemTotalAmount += quantity * rate
          stateCopy.trade_items[j].total_amount = itemTotalAmount
          tradeTotalAmount += itemTotalAmount
          tradeTotalQty += quantity
        }
        stateCopy.total_quantity = tradeTotalQty
        stateCopy.quantity_deducted =
          stateCopy.total_quantity *
          (Number(stateCopy.standard_deduction || 0) / 100)

        const deptCode = departmentData?.address?.state_region?.code
        const sellerCode = stateCopy.seller?.current_address?.state_region?.code
        const buyerCode = stateCopy.buyer?.current_address?.state_region?.code
        const isSameStateSupplier =
          deptCode && sellerCode ? deptCode === sellerCode : undefined
        const isSameStateBuyer =
          deptCode && buyerCode ? deptCode === buyerCode : undefined

        const round2 = (n: number) => Math.round(n * 100) / 100

        syncPackagingFlatToBlock(stateCopy)

        // --- Per-commodity GST ---
        let totalSupplierSgst = 0,
          totalSupplierCgst = 0,
          totalSupplierIgst = 0
        let totalBuyerSgst = 0,
          totalBuyerCgst = 0,
          totalBuyerIgst = 0

        const blocks = stateCopy.trade_commodity_items || []
        for (let cIdx = 0; cIdx < blocks.length; cIdx++) {
          const block = blocks[cIdx]
          const commodityTotals = getCommodityParticularsTotals(block)
          blocks[cIdx].total_quantity_kg = commodityTotals.total_quantity_kg
          const commodityGstBase =
            commodityTotals.total_cost *
            (1 - Number(stateCopy.standard_deduction || 0) / 100)

          const taxesConfig =
            block.commodity_variety?.organisation_commodity_variety_configs?.[0]
              ?.taxes || []

          const gst = block.gst || {
            supplier_sgst: 0,
            supplier_cgst: 0,
            supplier_igst: 0,
            supplier_sgst_percentage: 0,
            supplier_cgst_percentage: 0,
            supplier_igst_percentage: 0,
            supplier_gst_auto_calculated: true,
            seller_gst_attachments: [],
            buyer_sgst: 0,
            buyer_cgst: 0,
            buyer_igst: 0,
            buyer_sgst_percentage: 0,
            buyer_cgst_percentage: 0,
            buyer_igst_percentage: 0,
            buyer_gst_auto_calculated: true,
            buyer_gst_attachments: [],
            _gstFromBackend: false,
          }

          if (gst._gstFromBackend) {
            gst._gstFromBackend = false
            totalSupplierSgst += gst.supplier_sgst || 0
            totalSupplierCgst += gst.supplier_cgst || 0
            totalSupplierIgst += gst.supplier_igst || 0
            totalBuyerSgst += gst.buyer_sgst || 0
            totalBuyerCgst += gst.buyer_cgst || 0
            totalBuyerIgst += gst.buyer_igst || 0
            blocks[cIdx].gst = gst
            continue
          }

          let igstRate = 0,
            sgstRate = 0,
            cgstRate = 0
          taxesConfig.forEach((t: any) => {
            const name = (t.tax?.name || "").toUpperCase()
            const rate = (t.tax?.percentage || 0) / 100
            if (name === "IGST") igstRate = rate
            if (name === "SGST") sgstRate = rate
            if (name === "CGST") cgstRate = rate
          })

          if (taxesConfig.length === 0) {
            if (gst.supplier_gst_auto_calculated !== false) {
              gst.supplier_sgst = 0
              gst.supplier_cgst = 0
              gst.supplier_igst = 0
              gst.supplier_sgst_percentage = 0
              gst.supplier_cgst_percentage = 0
              gst.supplier_igst_percentage = 0
            } else {
              gst.supplier_sgst = round2(
                (commodityGstBase * Number(gst.supplier_sgst_percentage ?? 0)) /
                  100
              )
              gst.supplier_cgst = round2(
                (commodityGstBase * Number(gst.supplier_cgst_percentage ?? 0)) /
                  100
              )
              gst.supplier_igst = round2(
                (commodityGstBase * Number(gst.supplier_igst_percentage ?? 0)) /
                  100
              )
            }
            if (gst.buyer_gst_auto_calculated !== false) {
              gst.buyer_sgst = 0
              gst.buyer_cgst = 0
              gst.buyer_igst = 0
              gst.buyer_sgst_percentage = 0
              gst.buyer_cgst_percentage = 0
              gst.buyer_igst_percentage = 0
            } else {
              gst.buyer_sgst = round2(
                (commodityGstBase * Number(gst.buyer_sgst_percentage ?? 0)) /
                  100
              )
              gst.buyer_cgst = round2(
                (commodityGstBase * Number(gst.buyer_cgst_percentage ?? 0)) /
                  100
              )
              gst.buyer_igst = round2(
                (commodityGstBase * Number(gst.buyer_igst_percentage ?? 0)) /
                  100
              )
            }
          } else {
            // Supplier GST
            if (isSameStateSupplier !== undefined) {
              if (gst.supplier_gst_auto_calculated !== false) {
                if (isSameStateSupplier) {
                  gst.supplier_sgst = round2(commodityGstBase * sgstRate)
                  gst.supplier_cgst = round2(commodityGstBase * cgstRate)
                  gst.supplier_igst = 0
                  gst.supplier_sgst_percentage = round2(sgstRate * 100)
                  gst.supplier_cgst_percentage = round2(cgstRate * 100)
                  gst.supplier_igst_percentage = 0
                } else {
                  gst.supplier_sgst = 0
                  gst.supplier_cgst = 0
                  gst.supplier_igst = round2(commodityGstBase * igstRate)
                  gst.supplier_sgst_percentage = 0
                  gst.supplier_cgst_percentage = 0
                  gst.supplier_igst_percentage = round2(igstRate * 100)
                }
              } else {
                gst.supplier_sgst = round2(
                  (commodityGstBase *
                    Number(gst.supplier_sgst_percentage ?? 0)) /
                    100
                )
                gst.supplier_cgst = round2(
                  (commodityGstBase *
                    Number(gst.supplier_cgst_percentage ?? 0)) /
                    100
                )
                gst.supplier_igst = round2(
                  (commodityGstBase *
                    Number(gst.supplier_igst_percentage ?? 0)) /
                    100
                )
              }
            } else if (gst.supplier_gst_auto_calculated !== false) {
              gst.supplier_sgst = 0
              gst.supplier_cgst = 0
              gst.supplier_igst = 0
              gst.supplier_sgst_percentage = 0
              gst.supplier_cgst_percentage = 0
              gst.supplier_igst_percentage = 0
            } else {
              gst.supplier_sgst = round2(
                (commodityGstBase * Number(gst.supplier_sgst_percentage ?? 0)) /
                  100
              )
              gst.supplier_cgst = round2(
                (commodityGstBase * Number(gst.supplier_cgst_percentage ?? 0)) /
                  100
              )
              gst.supplier_igst = round2(
                (commodityGstBase * Number(gst.supplier_igst_percentage ?? 0)) /
                  100
              )
            }

            // Buyer GST
            if (isSameStateBuyer !== undefined) {
              if (gst.buyer_gst_auto_calculated !== false) {
                if (isSameStateBuyer) {
                  gst.buyer_sgst = round2(commodityGstBase * sgstRate)
                  gst.buyer_cgst = round2(commodityGstBase * cgstRate)
                  gst.buyer_igst = 0
                  gst.buyer_sgst_percentage = round2(sgstRate * 100)
                  gst.buyer_cgst_percentage = round2(cgstRate * 100)
                  gst.buyer_igst_percentage = 0
                } else {
                  gst.buyer_sgst = 0
                  gst.buyer_cgst = 0
                  gst.buyer_igst = round2(commodityGstBase * igstRate)
                  gst.buyer_sgst_percentage = 0
                  gst.buyer_cgst_percentage = 0
                  gst.buyer_igst_percentage = round2(igstRate * 100)
                }
              } else {
                gst.buyer_sgst = round2(
                  (commodityGstBase * Number(gst.buyer_sgst_percentage ?? 0)) /
                    100
                )
                gst.buyer_cgst = round2(
                  (commodityGstBase * Number(gst.buyer_cgst_percentage ?? 0)) /
                    100
                )
                gst.buyer_igst = round2(
                  (commodityGstBase * Number(gst.buyer_igst_percentage ?? 0)) /
                    100
                )
              }
            } else if (gst.buyer_gst_auto_calculated !== false) {
              gst.buyer_sgst = 0
              gst.buyer_cgst = 0
              gst.buyer_igst = 0
              gst.buyer_sgst_percentage = 0
              gst.buyer_cgst_percentage = 0
              gst.buyer_igst_percentage = 0
            } else {
              gst.buyer_sgst = round2(
                (commodityGstBase * Number(gst.buyer_sgst_percentage ?? 0)) /
                  100
              )
              gst.buyer_cgst = round2(
                (commodityGstBase * Number(gst.buyer_cgst_percentage ?? 0)) /
                  100
              )
              gst.buyer_igst = round2(
                (commodityGstBase * Number(gst.buyer_igst_percentage ?? 0)) /
                  100
              )
            }
          }

          totalSupplierSgst += gst.supplier_sgst
          totalSupplierCgst += gst.supplier_cgst
          totalSupplierIgst += gst.supplier_igst
          totalBuyerSgst += gst.buyer_sgst
          totalBuyerCgst += gst.buyer_cgst
          totalBuyerIgst += gst.buyer_igst

          blocks[cIdx].gst = gst
        }

        stateCopy.total_quantity_kg = round2(
          blocks.reduce(
            (acc, block) => acc + (Number(block.total_quantity_kg) || 0),
            0
          )
        )

        // Set aggregated top-level fields (used by Payment and sync)
        stateCopy.supplier_sgst = totalSupplierSgst
        stateCopy.supplier_cgst = totalSupplierCgst
        stateCopy.supplier_igst = totalSupplierIgst
        stateCopy.buyer_sgst = totalBuyerSgst
        stateCopy.buyer_cgst = totalBuyerCgst
        stateCopy.buyer_igst = totalBuyerIgst
        // --- Total amount = sum of per-commodity trade totals excl. commodity tax ---
        const isPackagingTrade = stateCopy.material_kind === "packaging"
        if (blocks.length && !isPackagingTrade) {
          let tradeTotalExclTax = 0
          for (const block of blocks) {
            const commodityTotals = getCommodityParticularsTotals(block)
            tradeTotalExclTax += commodityTotals.total_cost
          }
          stateCopy.total_amount = tradeTotalExclTax
        } else {
          stateCopy.total_amount = tradeTotalAmount
        }

        stateCopy.amount_deducted =
          stateCopy.total_amount *
          (Number(stateCopy.standard_deduction || 0) / 100)

        for (let j = 0; j < stateCopy.expenses.length; j++) {
          const expense = stateCopy.expenses[j]
          const expenseParams = {
            kind: expense?.kind?.id,
            buyerAmount: Number(expense.buyer?.value) || 0,
            sellerAmount: Number(expense.seller?.value) || 0,
            facilitatorAmount: Number(expense.facilitator?.value) || 0,
            finalAmount: stateCopy.total_amount,
            finalQuantity:
              stateCopy.total_quantity - stateCopy.quantity_deducted,
            totalQuantity: stateCopy.total_quantity,
          }
          expense.amount = expenseAmount(expenseParams)
          totalExpenseAmount += expense.amount
          expense.buyer_expense = expenseAmountBuyer(expenseParams)
          buyerExpenseAmount += expense.buyer_expense

          // Added seller expense calculation
          expense.seller_expense = expenseAmountSeller(expenseParams)
          supplierExpenseAmount += expense.seller_expense
        }

        for (let j = 0; j < stateCopy?.advance_payment?.length; j++) {
          const advance = stateCopy.advance_payment[j]
          advanceTotalAmount += Number(advance.amount) || 0
        }
        stateCopy.advance_amount = advanceTotalAmount
        stateCopy.expense_amount = totalExpenseAmount
        stateCopy.buyer_expense = buyerExpenseAmount
        stateCopy.supplier_expense = supplierExpenseAmount // Assigned to stateCopy
        stateCopy.buyer_amount =
          stateCopy.total_amount -
          stateCopy.amount_deducted +
          stateCopy.buyer_expense

        const totalCredit =
          creditData?.buyer_credit_wallet_info?.dynamic_available || 0

        stateCopy.exceeding =
          stateCopy.buyer_amount - totalCredit > 0
            ? stateCopy.buyer_amount - totalCredit
            : 0

        // Calculate Seller TDS
        if (
          stateCopy.supplier_tds_value &&
          deptData?.setu_trade_shared_entity?.[0]?.department_config
        ) {
          const seller_tds_percentage =
            deptData.setu_trade_shared_entity[0].department_config
              .seller_tds_percentage ?? 0
          const seller_tds_threshold =
            deptData.setu_trade_shared_entity[0].department_config
              .seller_tds_amount ?? 0
          const seller_receivable_current_year =
            stateCopy.seller?.seller_receivable_amount_current_year ?? 0

          const totalKg =
            (stateCopy.total_quantity_kg || 0) *
            (1 - Number(stateCopy.standard_deduction || 0) / 100)
          const supplier_rate = oData?.organisation_pricing?.supplier_rate ?? 0
          const tax_percentage =
            oData?.organisation_pricing?.tax_percentage ?? 0
          const supplierCharges =
            totalKg * supplier_rate +
            (totalKg * supplier_rate * tax_percentage) / 100

          const finalTotal = stateCopy.total_amount - stateCopy.amount_deducted
          const tdsBaseAmount =
            finalTotal - (stateCopy.supplier_expense || 0) - supplierCharges

          const totalAmount = seller_receivable_current_year + finalTotal
          if (totalAmount > seller_tds_threshold) {
            const exceedingAmount = totalAmount - seller_tds_threshold
            const tdsApplicableAmount = Math.min(exceedingAmount, tdsBaseAmount)
            stateCopy.seller_tds =
              (tdsApplicableAmount * seller_tds_percentage) / 100
          } else {
            stateCopy.seller_tds = 0
          }
        } else {
          stateCopy.seller_tds = null
        }

        const supplierExpenseForGst = Number(stateCopy.supplier_expense || 0)
        const buyerExpenseForGst = Number(stateCopy.buyer_expense || 0)

        // New parent trade with multi-commodity: never auto-derive expense
        // GST. Zero it out and let the user enter the values manually.
        const isMultiCommodityManualExpenseGst =
          Number.isNaN(idNum) &&
          !stateCopy.parent_trade?.id &&
          hasMultipleVarietiesWithGst(stateCopy)

        if (isMultiCommodityManualExpenseGst) {
          stateCopy.expense_supplier_sgst = 0
          stateCopy.expense_supplier_cgst = 0
          stateCopy.expense_supplier_igst = 0
          stateCopy.expense_supplier_sgst_percentage = 0
          stateCopy.expense_supplier_cgst_percentage = 0
          stateCopy.expense_supplier_igst_percentage = 0
          stateCopy.expense_buyer_sgst = 0
          stateCopy.expense_buyer_cgst = 0
          stateCopy.expense_buyer_igst = 0
          stateCopy.expense_buyer_sgst_percentage = 0
          stateCopy.expense_buyer_cgst_percentage = 0
          stateCopy.expense_buyer_igst_percentage = 0
          stateCopy.expense_supplier_gst_auto_calculated = false
          stateCopy.expense_buyer_gst_auto_calculated = false
        } else {
          // Expense GST is independent from trade GST and is applied
          // on aggregated supplier/buyer expense amounts.
          const expenseTaxesConfig =
            stateCopy.commodity_variety
              ?.organisation_commodity_variety_configs?.[0]?.taxes || []
          const canCalcExpenseSupplierGst = deptCode && sellerCode
          const canCalcExpenseBuyerGst =
            deptCode && buyerCode && stateCopy.buyer?.id

          const expenseRound2 = (n: number) => Math.round(n * 100) / 100

          if (expenseTaxesConfig.length === 0) {
            if (stateCopy.expense_supplier_gst_auto_calculated !== false) {
              stateCopy.expense_supplier_sgst = 0
              stateCopy.expense_supplier_cgst = 0
              stateCopy.expense_supplier_igst = 0
              stateCopy.expense_supplier_sgst_percentage = 0
              stateCopy.expense_supplier_cgst_percentage = 0
              stateCopy.expense_supplier_igst_percentage = 0
            } else {
              const sgstPct =
                Number(stateCopy.expense_supplier_sgst_percentage ?? 0) / 100
              const cgstPct =
                Number(stateCopy.expense_supplier_cgst_percentage ?? 0) / 100
              const igstPct =
                Number(stateCopy.expense_supplier_igst_percentage ?? 0) / 100
              stateCopy.expense_supplier_sgst = expenseRound2(
                supplierExpenseForGst * sgstPct
              )
              stateCopy.expense_supplier_cgst = expenseRound2(
                supplierExpenseForGst * cgstPct
              )
              stateCopy.expense_supplier_igst = expenseRound2(
                supplierExpenseForGst * igstPct
              )
            }

            if (stateCopy.expense_buyer_gst_auto_calculated !== false) {
              stateCopy.expense_buyer_sgst = 0
              stateCopy.expense_buyer_cgst = 0
              stateCopy.expense_buyer_igst = 0
              stateCopy.expense_buyer_sgst_percentage = 0
              stateCopy.expense_buyer_cgst_percentage = 0
              stateCopy.expense_buyer_igst_percentage = 0
            } else {
              const sgstPct =
                Number(stateCopy.expense_buyer_sgst_percentage ?? 0) / 100
              const cgstPct =
                Number(stateCopy.expense_buyer_cgst_percentage ?? 0) / 100
              const igstPct =
                Number(stateCopy.expense_buyer_igst_percentage ?? 0) / 100
              stateCopy.expense_buyer_sgst = expenseRound2(
                buyerExpenseForGst * sgstPct
              )
              stateCopy.expense_buyer_cgst = expenseRound2(
                buyerExpenseForGst * cgstPct
              )
              stateCopy.expense_buyer_igst = expenseRound2(
                buyerExpenseForGst * igstPct
              )
            }
          } else {
            let igstRate = 0,
              sgstRate = 0,
              cgstRate = 0
            expenseTaxesConfig.forEach((t: any) => {
              const name = (t.tax?.name || "").toUpperCase()
              const rate = (t.tax?.percentage || 0) / 100
              if (name === "IGST") igstRate = rate
              if (name === "SGST") sgstRate = rate
              if (name === "CGST") cgstRate = rate
            })

            if (!canCalcExpenseSupplierGst) {
              if (stateCopy.expense_supplier_gst_auto_calculated !== false) {
                stateCopy.expense_supplier_sgst = 0
                stateCopy.expense_supplier_cgst = 0
                stateCopy.expense_supplier_igst = 0
                stateCopy.expense_supplier_sgst_percentage = 0
                stateCopy.expense_supplier_cgst_percentage = 0
                stateCopy.expense_supplier_igst_percentage = 0
              }
            } else if (
              stateCopy.expense_supplier_gst_auto_calculated !== false
            ) {
              const sameState = deptCode === sellerCode
              if (sameState) {
                stateCopy.expense_supplier_sgst = expenseRound2(
                  supplierExpenseForGst * sgstRate
                )
                stateCopy.expense_supplier_cgst = expenseRound2(
                  supplierExpenseForGst * cgstRate
                )
                stateCopy.expense_supplier_igst = 0
                stateCopy.expense_supplier_sgst_percentage = expenseRound2(
                  sgstRate * 100
                )
                stateCopy.expense_supplier_cgst_percentage = expenseRound2(
                  cgstRate * 100
                )
                stateCopy.expense_supplier_igst_percentage = 0
              } else {
                stateCopy.expense_supplier_sgst = 0
                stateCopy.expense_supplier_cgst = 0
                stateCopy.expense_supplier_igst = expenseRound2(
                  supplierExpenseForGst * igstRate
                )
                stateCopy.expense_supplier_sgst_percentage = 0
                stateCopy.expense_supplier_cgst_percentage = 0
                stateCopy.expense_supplier_igst_percentage = expenseRound2(
                  igstRate * 100
                )
              }
            } else {
              const sgstPct =
                Number(stateCopy.expense_supplier_sgst_percentage ?? 0) / 100
              const cgstPct =
                Number(stateCopy.expense_supplier_cgst_percentage ?? 0) / 100
              const igstPct =
                Number(stateCopy.expense_supplier_igst_percentage ?? 0) / 100
              stateCopy.expense_supplier_sgst = expenseRound2(
                supplierExpenseForGst * sgstPct
              )
              stateCopy.expense_supplier_cgst = expenseRound2(
                supplierExpenseForGst * cgstPct
              )
              stateCopy.expense_supplier_igst = expenseRound2(
                supplierExpenseForGst * igstPct
              )
            }

            if (!canCalcExpenseBuyerGst) {
              if (stateCopy.expense_buyer_gst_auto_calculated !== false) {
                stateCopy.expense_buyer_sgst = 0
                stateCopy.expense_buyer_cgst = 0
                stateCopy.expense_buyer_igst = 0
                stateCopy.expense_buyer_sgst_percentage = 0
                stateCopy.expense_buyer_cgst_percentage = 0
                stateCopy.expense_buyer_igst_percentage = 0
              }
            } else if (stateCopy.expense_buyer_gst_auto_calculated !== false) {
              const sameState = deptCode === buyerCode
              if (sameState) {
                stateCopy.expense_buyer_sgst = expenseRound2(
                  buyerExpenseForGst * sgstRate
                )
                stateCopy.expense_buyer_cgst = expenseRound2(
                  buyerExpenseForGst * cgstRate
                )
                stateCopy.expense_buyer_igst = 0
                stateCopy.expense_buyer_sgst_percentage = expenseRound2(
                  sgstRate * 100
                )
                stateCopy.expense_buyer_cgst_percentage = expenseRound2(
                  cgstRate * 100
                )
                stateCopy.expense_buyer_igst_percentage = 0
              } else {
                stateCopy.expense_buyer_sgst = 0
                stateCopy.expense_buyer_cgst = 0
                stateCopy.expense_buyer_igst = expenseRound2(
                  buyerExpenseForGst * igstRate
                )
                stateCopy.expense_buyer_sgst_percentage = 0
                stateCopy.expense_buyer_cgst_percentage = 0
                stateCopy.expense_buyer_igst_percentage = expenseRound2(
                  igstRate * 100
                )
              }
            } else {
              const sgstPct =
                Number(stateCopy.expense_buyer_sgst_percentage ?? 0) / 100
              const cgstPct =
                Number(stateCopy.expense_buyer_cgst_percentage ?? 0) / 100
              const igstPct =
                Number(stateCopy.expense_buyer_igst_percentage ?? 0) / 100
              stateCopy.expense_buyer_sgst = expenseRound2(
                buyerExpenseForGst * sgstPct
              )
              stateCopy.expense_buyer_cgst = expenseRound2(
                buyerExpenseForGst * cgstPct
              )
              stateCopy.expense_buyer_igst = expenseRound2(
                buyerExpenseForGst * igstPct
              )
            }
          }
        }

        form.setValues(stateCopy)
      }, 500),

    [deptData, oData, departmentData, creditData]
  )

  // On a brand-new parent trade with multi-commodity: expense GST checkboxes
  // are locked to manual entry (unchecked + disabled) and the notice popup is
  // shown once. Existing trades and child pattis are not affected.
  useEffect(() => {
    if (!expenseGstManualOnlyOnCreate) return

    if (
      form.values.expense_supplier_gst_auto_calculated !== false ||
      form.values.expense_buyer_gst_auto_calculated !== false
    ) {
      const nextValues = {
        ...form.values,
        expense_supplier_gst_auto_calculated: false,
        expense_buyer_gst_auto_calculated: false,
      }
      form.setFieldValue("expense_supplier_gst_auto_calculated", false)
      form.setFieldValue("expense_buyer_gst_auto_calculated", false)
      setTimeout(() => runComputations(nextValues), 0)
    }

    if (!expenseGstManualNoticeShown.current && !isCompletelyDisabled) {
      expenseGstManualNoticeShown.current = true
      setShowExpenseGstManualNotice(true)
    }
  }, [expenseGstManualOnlyOnCreate])

  useEffect(() => {
    if (hasAppliedRedirectState.current) return

    const vehicleId = getVehicleIdFromRedirectUrl(searchParams)
    const rawPersisted = localStorage.getItem(
      REGISTRATION_REDIRECT_FORM_STATE_KEY
    )
    const restored = parsePersistedRegistrationFormState(rawPersisted)
    const isNewTrade = !idNum || Number.isNaN(idNum)

    if (!vehicleId && (!restored || !isNewTrade)) return

    hasAppliedRedirectState.current = true

    const applyRedirectState = async () => {
      let currentState = form.values

      if (restored) {
        form.setValues(restored)
        currentState = restored
        hasRestoredState.current = true
        window.setTimeout(() => {
          localStorage.removeItem(REGISTRATION_REDIRECT_FORM_STATE_KEY)
        }, 1000)
      }

      if (!vehicleId) {
        if (restored) runComputations(restored)
        return
      }

      const { data } = await client
        .query(VehicleDetailDocument, { id: vehicleId })
        .toPromise()

      if (data?.vehicle_detail) {
        const vehicle = {
          id: data.vehicle_detail.id,
          name: data.vehicle_detail.registration_number,
          codename: data.vehicle_detail.registration_number,
        }

        const { data: sharedData } = await upsertSharedEntity({
          reference_id: vehicle.codename,
          group_name: SharedEntityGroup.StarlingVehicle,
          name: vehicle.name,
        })

        if (sharedData?.insert_setu_trade_shared_entity_one?.id) {
          const vehicleWithSharedId = {
            ...vehicle,
            shared_id: sharedData.insert_setu_trade_shared_entity_one.id,
          }
          const finalState = { ...currentState, vehicle: vehicleWithSharedId }
          form.setValues(finalState)
          runComputations(finalState)
          return
        }
      }

      if (restored) runComputations(restored)
    }

    applyRedirectState()
  }, [searchParams, client, form, runComputations, upsertSharedEntity, idNum])

  useEffect(() => {
    if (hasRestoredState.current) return

    if (
      tradeId ||
      offerData?.offers_by_pk?.id ||
      demandData?.demand_by_pk?.id
    ) {
      setRemovedTradeItems({})
      updateTradeState(
        dData?.trade_by_pk,
        bookingData,
        offerData?.offers_by_pk as any,
        demandData?.demand_by_pk as any
      )
    }
  }, [
    tradeId,
    bookingSummaryId,
    bookingData,
    offerData,
    demandData,
    receivableData,
  ])

  function inputProps(name: string, sharedEntity?: SharedEntity) {
    return {
      value: get(state, name, ""),
      error: get(form.errors, name, "") as any,
      onChange: async (e: any) => {
        const stateCopy = cloneDeep(state)
        let facilitator = stateCopy.seller?.companies?.[0]?.company
        if (name === "mode" && e === "facilitator" && facilitator) {
          const { data: facilitatorShared } = await upsertSharedEntity({
            reference_id: facilitator.codename,
            group_name: SharedEntityGroup.KiteCompany,
            name: facilitator.name,
          })
          let shared_id =
            facilitatorShared?.insert_setu_trade_shared_entity_one?.id
          set(stateCopy, "facilitator", { ...facilitator, shared_id })
        } else if (
          (name === "mode" && e === "direct") ||
          (name === "mode" && e === "facilitator" && !facilitator) ||
          (name === "seller" && !e?.companies.length)
        ) {
          set(stateCopy, "facilitator", null)
          set(stateCopy, "mode", "direct")
        }

        if (sharedEntity && e?.codename && e?.name && !e?.shared_id) {
          const { data } = await upsertSharedEntity({
            reference_id: e.codename,
            group_name: sharedEntity.group,
            name: e.name,
          })
          set(stateCopy, name, {
            ...e,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
            seller_receivable_amount_current_year:
              data?.insert_setu_trade_shared_entity_one
                ?.seller_receivable_amount_current_year,
          })
        } else {
          set(stateCopy, name, e?.target ? e.target.value : e)
          if (name === "seller" && e?.seller_receivable_amount_current_year) {
            set(
              stateCopy,
              "seller.seller_receivable_amount_current_year",
              e?.seller_receivable_amount_current_year
            )
          }
        }
        const shouldSyncNested =
          stateCopy.trade_commodity_items?.length &&
          (name.startsWith("trade_commodity_items") ||
            name === "quantity_unit" ||
            name === "allow_multiple_commodity")
        if (shouldSyncNested) {
          if (name === "quantity_unit") {
            stateCopy.trade_commodity_items.forEach((blk: any) => {
              blk.quantity_unit = stateCopy.quantity_unit
              blk.grades?.forEach((grade: any) => {
                grade.quantity_unit = stateCopy.quantity_unit
                if (!grade.rate_unit) grade.rate_unit = stateCopy.quantity_unit
              })
            })
          }
          const synced = syncNestedTradeToFormValues(stateCopy)
          runComputations(synced)
          setState(synced)
          return
        }
        if (stateCopy.material_kind === "packaging") {
          syncPackagingFlatToBlock(stateCopy)
        }
        runComputations(stateCopy)
        setState(stateCopy)
      },
    }
  }

  // Auto select grade for packaging material type
  useEffect(() => {
    async function initGrade() {
      if (isPackaging && packagingGrades?.variety_grade?.length) {
        let firstGrade = packagingGrades.variety_grade[0]

        const needsUpdate = state?.trade_items?.some(
          (item: any) =>
            !item.variety_grade ||
            (item.variety_grade.id !== firstGrade.id &&
              !packagingGrades.variety_grade.some(
                g => g.id === item.variety_grade?.id
              ))
        )

        if (needsUpdate) {
          let selection = { ...firstGrade } as any
          if (!selection.shared_id) {
            if (fetchingGradeRef.current.has(firstGrade.id)) return
            fetchingGradeRef.current.add(firstGrade.id)

            try {
              const { data } = await upsertSharedEntity({
                reference_id: selection.codename,
                group_name: SharedEntityGroup.KiteGrade,
                name: selection.name,
              })
              selection.shared_id =
                data?.insert_setu_trade_shared_entity_one?.id
            } finally {
              fetchingGradeRef.current.delete(firstGrade.id)
            }
          }

          setState((prev: any) => {
            if (!prev?.trade_items) return prev
            let changed = false
            const newItems = cloneDeep(prev.trade_items)
            newItems.forEach((item: any) => {
              if (
                !item.variety_grade ||
                (item.variety_grade.id !== firstGrade.id &&
                  !packagingGrades.variety_grade.some(
                    g => g.id === item.variety_grade?.id
                  ))
              ) {
                item.variety_grade = selection
                changed = true
              }
            })
            if (changed) {
              const newState = { ...prev, trade_items: newItems }
              if (isPackaging) syncPackagingFlatToBlock(newState)
              setTimeout(() => runComputations(newState), 0)
              return newState
            }
            return prev
          })
        }
      }
    }
    initGrade()
  }, [
    packagingGrades,
    isPackaging,
    runComputations,
    state?.trade_items?.length,
  ])

  const finalTotal =
    (form.values.total_amount || 0) - (form.values.amount_deducted || 0)

  const onSubmit = async ({
    createDeviation = false,
    createOverdue = false,
  }) => {
    const hasOrgConfig =
      (form.values.commodity_variety
        ?.organisation_commodity_variety_configs?.[0]?.taxes?.length ?? 0) > 0

    // Supplier state code is required when org config has taxes and a seller is selected
    if (
      hasOrgConfig &&
      state?.seller?.id &&
      !state?.seller?.current_address?.state_region
    ) {
      toast.custom(
        <Toast
          title="Missing Supplier State Code"
          intent="danger"
          subtitle="Supplier is missing a state region code. GST cannot be calculated."
        />
      )
      return
    }

    // Buyer state code is only required when a buyer is selected
    if (
      hasOrgConfig &&
      state?.buyer?.id &&
      !state?.buyer?.current_address?.state_region
    ) {
      toast.custom(
        <Toast
          title="Missing Buyer State Code"
          intent="danger"
          subtitle="Buyer is missing a state region code. GST cannot be calculated."
        />
      )
      return
    }

    const validation = form.validate()

    if (validation.hasErrors) {
      toast.custom(
        <Toast
          title="Please fix validation errors"
          intent="danger"
          subtitle={Object.values(validation.errors)?.[0]}
        />
      )
      return
    }

    const { data: departmentData } = await upsertSharedEntity({
      group_name: SharedEntityGroup.KiteDepartment,
      name: currentDept?.name,
      reference_id: currentDept?.id,
    })

    const std = Number(state?.standard_deduction)
    if (
      !isEmpty(removedExpenseItems) &&
      !newHishobPatti &&
      !newBookingPatti &&
      !newInwardPatti &&
      !newStoragePatti
    )
      await deleteExpenseItem({
        ids: Object.keys(removedExpenseItems),
      })
    const { data: iData, error } = await upsertTrade({
      input: {
        id:
          id === "new" ||
          id === "newInward" ||
          isHishobPatti ||
          (newHishobPatti && isStatusGenerateInward) ||
          (newHishobPatti && isStatusInwardConfirmed) ||
          isInwardPatti ||
          newBookingPatti ||
          newStoragePatti
            ? undefined
            : state?.id,
        is_intercompany_transaction:
          state?.is_intercompany_transaction === "yes"
            ? true
            : state?.is_intercompany_transaction === "no"
            ? false
            : undefined,
        offer_id: state?.offerId,
        demand_id: state?.demandId,
        use_overdraft: createDeviation,
        use_overdue_approval: createOverdue,
        material_kind: state?.material_kind,
        parent_trade_id:
          isHishobPatti ||
          (newHishobPatti && isStatusGenerateInward) ||
          (newHishobPatti && isStatusInwardConfirmed) ||
          isInwardPatti ||
          newBookingPatti ||
          newStoragePatti
            ? state?.id
            : undefined,
        packaging: state?.packaging,
        is_multiple_commodity: Boolean(state?.allow_multiple_commodity),
        supplier_expense_gst_auto_calculated:
          form.values.expense_supplier_gst_auto_calculated ?? true,
        buyer_expense_gst_auto_calculated:
          form.values.expense_buyer_gst_auto_calculated ?? true,
        ...(form.values.supplier_tds_value && {
          seller_tds_percentage:
            deptData?.setu_trade_shared_entity?.[0]?.department_config
              ?.seller_tds_percentage,
        }),
        ...(!form.values.supplier_tds_value && { seller_tds_percentage: null }),
        kind: isHishobPatti ? TradeKinds.HishobPatti : state?.kind,
        mode: state?.mode,
        standard_deduction: std ?? null,
        buyer_tnc: state?.buyer_tnc,
        seller_tnc: state?.seller_tnc,
        vehicle_id: state?.vehicle?.shared_id,
        current_status_id: "Draft",
        attachments: state?.attachments?.map(attachment => attachment.media.id),
        start_date:
          state?.start_date && dayjs(state?.start_date).format("YYYY-MM-DD"),
        end_date:
          state?.end_date && dayjs(state?.end_date).format("YYYY-MM-DD"),
        expiry: state?.expiry && dayjs(state?.expiry).format("YYYY-MM-DD"),
        facilitator_id: state?.facilitator?.shared_id,
        department_id: departmentData?.insert_setu_trade_shared_entity_one?.id,
        seller_id: state?.seller?.shared_id,
        buyer_id:
          state?.kind === TradeKinds.StoragePatti
            ? Boolean(
                dData?.trade_by_pk?.parent_trade?.demand_id ||
                  dData?.trade_by_pk?.demand_id ||
                  dData?.trade_by_pk?.buyer?.id
              )
              ? state?.buyer?.shared_id
              : undefined
            : state?.buyer?.shared_id,
        advance_po: state?.advance_po ?? false,
        storage_location_id: state?.storage_location?.id,
        storage_sub_location_id: state?.storage_sub_location?.id,
        items: (() => {
          const blocks = state?.trade_commodity_items?.length
            ? state.trade_commodity_items
            : buildTradeCommodityItemsFromState(state)
          const itemInputs = buildTradeItemInputsFromBlocks(blocks, {
            defaultCurrency,
            qcResults: qcForm.values?.results,
            removedTradeItems,
            includeId: !(
              isHishobPatti ||
              isInwardPatti ||
              newBookingPatti ||
              newStoragePatti ||
              (newHishobPatti && isStatusGenerateInward) ||
              (newHishobPatti && isStatusInwardConfirmed)
            ),
            parentTradeItemIdFor:
              newHishobPatti ||
              newBookingPatti ||
              newInwardPatti ||
              newStoragePatti
                ? (grade: any) => grade.id
                : undefined,
          })
          return itemInputs.length ? itemInputs : undefined
        })(),
        expenses: state?.expenses?.filter(
          i => i.seller || i.buyer || i.facilitator
        )?.length
          ? state?.expenses?.reduce((prev, expense) => {
              const commonProperties = {
                expense_parameter_id: expense.expense_parameter?.id,
                kind: expense.kind?.id,
              }
              if (expense.buyer?.value) {
                prev.push({
                  ...commonProperties,
                  ...(!isHishobPatti &&
                    !isInwardPatti &&
                    !newBookingPatti &&
                    !newStoragePatti &&
                    !(newHishobPatti && isStatusGenerateInward) &&
                    !(newHishobPatti && isStatusInwardConfirmed) && {
                      id: expense.buyer.id,
                    }),
                  paid_by: "buyer",
                  value: expense.buyer.value,
                })
              }
              if (expense.seller?.value) {
                prev.push({
                  ...commonProperties,
                  ...(!isHishobPatti &&
                    !isInwardPatti &&
                    !newBookingPatti &&
                    !newStoragePatti &&
                    !(newHishobPatti && isStatusGenerateInward) &&
                    !(newHishobPatti && isStatusInwardConfirmed) && {
                      id: expense.seller.id,
                    }),
                  paid_by: "seller",
                  value: expense.seller.value,
                })
              }
              if (expense.facilitator?.value) {
                prev.push({
                  ...commonProperties,
                  ...(!isHishobPatti &&
                    !isInwardPatti &&
                    !newBookingPatti &&
                    !newStoragePatti &&
                    !(newHishobPatti && isStatusGenerateInward) &&
                    !(newHishobPatti && isStatusInwardConfirmed) && {
                      id: expense.facilitator.id,
                    }),
                  paid_by: "facilitator",
                  value: expense.facilitator.value,
                })
              }
              return prev
            }, [])
          : undefined,
        expense_taxes: (() => {
          const taxRows: {
            role: string
            tax_name: string
            percentage: number
          }[] = []
          const push = (
            role: string,
            tax_name: string,
            percentage: number,
            amount: number
          ) => {
            if (percentage > 0 || amount > 0) {
              taxRows.push({
                role,
                tax_name,
                percentage,
              })
            }
          }
          const isNewPatti =
            isHishobPatti ||
            isInwardPatti ||
            newBookingPatti ||
            newStoragePatti ||
            (newHishobPatti && isStatusGenerateInward) ||
            (newHishobPatti && isStatusInwardConfirmed)

          push(
            "seller",
            "SGST",
            state.expense_supplier_sgst_percentage ?? 0,
            state.expense_supplier_sgst ?? 0
          )
          push(
            "seller",
            "CGST",
            state.expense_supplier_cgst_percentage ?? 0,
            state.expense_supplier_cgst ?? 0
          )
          push(
            "seller",
            "IGST",
            state.expense_supplier_igst_percentage ?? 0,
            state.expense_supplier_igst ?? 0
          )
          push(
            "buyer",
            "SGST",
            state.expense_buyer_sgst_percentage ?? 0,
            state.expense_buyer_sgst ?? 0
          )
          push(
            "buyer",
            "CGST",
            state.expense_buyer_cgst_percentage ?? 0,
            state.expense_buyer_cgst ?? 0
          )
          push(
            "buyer",
            "IGST",
            state.expense_buyer_igst_percentage ?? 0,
            state.expense_buyer_igst ?? 0
          )

          if (!taxRows.length) return undefined
          return taxRows
        })(),

        advance_payments:
          canAddAdvancePayment(state?.kind) && state?.advance_payment?.length
            ? state?.advance_payment.map(advance => ({
                ...(!isHishobPatti &&
                  !isInwardPatti &&
                  !newBookingPatti &&
                  !newStoragePatti &&
                  !(newHishobPatti && isStatusGenerateInward) &&
                  !(newHishobPatti && isStatusInwardConfirmed) &&
                  advance.id && {
                    id: advance.id,
                  }),
                parameter_id: advance.parameter?.id,
                amount: advance.amount,
                bourn_by: advance.bourn_by?.id,
              }))
            : undefined,

        credit_notes: state?.credit_notes?.length
          ? state?.credit_notes.map(credit => ({
              ...(!isHishobPatti &&
                !isInwardPatti &&
                !newBookingPatti &&
                !newStoragePatti &&
                !(newHishobPatti && isStatusGenerateInward) &&
                !(newHishobPatti && isStatusInwardConfirmed) &&
                credit.id && {
                  id: credit.id,
                }),
              amount: credit.amount,
              remarks: credit.remarks,
            }))
          : undefined,
        officer_id: state?.officer?.shared_id,
        junior_officer_id: state?.junior_officer?.shared_id,
        buyer_gst_attachments: state?.buyer_gst_attachments?.length
          ? state.buyer_gst_attachments.map((att: any) => ({
              ...(!isHishobPatti &&
                !isInwardPatti &&
                !newBookingPatti &&
                !newStoragePatti &&
                !(newHishobPatti && isStatusGenerateInward) &&
                !(newHishobPatti && isStatusInwardConfirmed) &&
                att.id && { id: att.id }),
              media_id: att.media.id,
            }))
          : undefined,
        seller_gst_attachments: state?.seller_gst_attachments?.length
          ? state.seller_gst_attachments.map((att: any) => att?.media?.id)
          : undefined,
      },
    })
    if (error || !iData) {
      return toast.custom(
        <Toast title={"Unable to create the trade request"} intent="danger" />
      )
    }

    if (
      iData?.add_update_trade?.trade?.id &&
      !createDeviation &&
      !createOverdue
    ) {
      navigate(`/d/trade-requests/${iData?.add_update_trade?.trade?.id}`)
      setOpen(true)
      toast.custom(<Toast title={"Trade created successfully!"} />)
    }
    if (
      iData?.add_update_trade?.trade?.id &&
      (createDeviation || createOverdue)
    ) {
      navigate(AppRoutes.tradeRequests)
      toast.custom(
        <Toast
          title={`Trade created under ${
            createDeviation ? "deviation" : "overdue approval"
          } successfully!`}
        />
      )
    }
  }

  const canViewTDS = state?.seller_tds
    ? true
    : state?.seller?.seller_receivable_amount_current_year + finalTotal >=
      deptData?.setu_trade_shared_entity?.[0]?.department_config
        ?.seller_tds_amount

  const totalKg =
    (state?.total_quantity_kg || 0) *
    (1 - Number(form.values.standard_deduction || 0) / 100)
  const supplier_rate = oData?.organisation_pricing?.supplier_rate ?? 0
  const tax_percentage = oData?.organisation_pricing?.tax_percentage ?? 0
  const calculatedCharges =
    totalKg * supplier_rate + (totalKg * supplier_rate * tax_percentage) / 100

  const sellerTdsAmount = state?.seller_tds ?? 0
  const supplierCharges =
    state?.id &&
    !(newHishobPatti || newInwardPatti || newStoragePatti || newBookingPatti)
      ? String(state?.supplier_charge ?? 0)
      : isNaN(calculatedCharges)
      ? "0"
      : calculatedCharges.toFixed(2)

  return (
    <>
      <header className="bg-gray-100 shadow-sm px-3 py-2 border-b-gray-300 border-b flex justify-between items-center min-w-0 gap-2">
        <SpinnerOverlay
          show={fetching || creditFetching || sellerFetching}
          className="fixed"
        />
        <Breadcrumbs
          items={[
            {
              text: "Trade Requests",
              link: "/d/trade-requests",
              isActive: false,
            },
            {
              text: id?.toString() || "New",
              link: `/d/trade-requests/${id}`,
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
        <Link to={`?timeline=${id}`}>Timeline</Link>
      </header>
      <Group className="flex flex-col md:flex-row gap-3 md:gap-4 px-2 sm:px-3 md:px-5 pt-4 md:pt-8 pb-24 md:pb-16 relative">
        <SideBar
          key={state?.id || `trades_${state?.trades?.length}`}
          id={idNum}
          disableNew={!moreTrades}
          kind={state?.parent_trade?.kind || dData?.trade_by_pk?.kind}
          demandId={
            demandId || state?.parent_trade?.demand_id || state?.demand_id
          }
          offerId={offerId || state?.parent_trade?.offer_id || state?.offer_id}
          allOffers={newHishobPatti ? offerOrderData?.offers : []}
        />

        <div className="w-full md:w-[calc(100%-316px)] lg:w-[70%] md:mx-0 lg:mx-auto min-w-0">
          <Toast
            className="mb-5 min-w-full"
            hidden={
              !dData?.trade_by_pk?.over_draft?.logs?.[0]?.id ||
              newHishobPatti ||
              newBookingPatti
            }
            intent={deviationRejected ? "danger" : "primary"}
            variant="minimal"
            title={
              dData?.trade_by_pk?.over_draft?.logs?.[0]?.status +
              " by " +
              dData?.trade_by_pk?.over_draft?.logs?.[0]?.user?.name
            }
          />

          {canCreateNewOverdueApproval ? (
            <Toast
              className="mb-5 min-w-full"
              intent="danger"
              variant="minimal"
              title={
                <div>
                  {`Your Existing Overdue amount is ${inr(
                    creditData?.buyer_credit_wallet_info?.overdue_amount || 0
                  )}. ${
                    creditData?.buyer_credit_wallet_info
                      ?.requested_overdue_approval_count
                      ? `Overdue Approval request Pending!`
                      : ""
                  }`}
                </div>
              }
            />
          ) : null}

          <WhoIsTrading
            form={form}
            creditForm={creditForm}
            inputProps={inputProps}
            showBuyer={
              state?.kind === TradeKinds.StoragePatti
                ? Boolean(
                    dData?.trade_by_pk?.parent_trade?.demand_id ||
                      dData?.trade_by_pk?.demand_id ||
                      dData?.trade_by_pk?.buyer?.id
                  )
                : true
            }
            isDisabled={
              ((isCompletelyDisabled || isInheritedHishobPatti) &&
                !isNewStorageHishobPatti) ||
              isOrderPatti ||
              createSauda ||
              newStoragePatti ||
              inwardFromDemand
            }
            useCredit={useCredit}
            setPackingOpen={() => setPackingOpen(true)}
            showAllowMultipleCommodity={!isPackaging && canAddMultiCommodity}
            disableBuyer={
              (isStatusGenerateInward || isStatusInwardConfirmed) &&
              state?.kind === "final" &&
              state?.parent_trade?.kind === "inward"
                ? false
                : isNewStorageHishobPatti
                ? false
                : newHishobPatti
                ? false
                : isStatusGenerateSale ||
                  isStatusInvoiced ||
                  isCompletelyDisabled ||
                  isInheritedHishobPatti ||
                  newStoragePatti ||
                  inwardFromDemand
            }
            creditFetching={creditFetching}
          />
          <HowIsItTraded
            form={form}
            inputProps={inputProps}
            showBookingKinds
            showInwardKinds={newInwardPatti}
            isDisabled={
              isStatusGenerateSale ||
              isStatusInvoiced ||
              isCompletelyDisabled ||
              isInheritedHishobPatti
            }
            showIntercompany={!isPackaging && !offerId && !demandId}
            disableIntercompany={isChildPatti}
          />
          <WhoOverseesTrading
            inputProps={inputProps}
            isDisabled={
              isStatusGenerateSale ||
              isStatusInvoiced ||
              isCompletelyDisabled ||
              isInheritedHishobPatti ||
              disableOfficerField ||
              isOrderPatti ||
              createSauda ||
              newStoragePatti ||
              inwardFromDemand
            }
          />
          {!isStoragePatti && isPackaging ? null : (
            <WhatIsBeingTraded
              form={form}
              inputProps={inputProps}
              isDisabled={
                isStatusGenerateSale ||
                isStatusInvoiced ||
                isCompletelyDisabled ||
                isInheritedHishobPatti ||
                isOrderPatti ||
                createSauda ||
                newStoragePatti ||
                inwardFromDemand
              }
              isMulti
            />
          )}
          <FormCard title="When is it traded">
            <div
              className={"grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"}
            >
              <FormDateInput
                label="Start Date of Trade"
                {...inputProps("start_date")}
                disabled={
                  isStatusGenerateSale ||
                  isStatusInvoiced ||
                  (isCompletelyDisabled && !isHishobPatti)
                }
                required
                popoverPanelClassName={classNames(
                  isCompletelyDisabled ? "hidden" : ""
                )}
              />
              <FormDateInput
                label="End Date of Trade"
                {...inputProps("end_date")}
                disabled={
                  isStatusGenerateSale ||
                  isStatusInvoiced ||
                  (isCompletelyDisabled && !isHishobPatti)
                }
                popoverPanelClassName={classNames(
                  isCompletelyDisabled ? "hidden" : ""
                )}
              />
              {isHishobPatti ? null : (
                <FormDateInput
                  label="Validity of Trade"
                  required
                  {...inputProps("expiry")}
                  disabled={
                    isStatusGenerateSale ||
                    isStatusInvoiced ||
                    (isCompletelyDisabled && !isHishobPatti)
                  }
                  popoverPanelClassName={classNames(
                    isCompletelyDisabled ? "hidden" : ""
                  )}
                />
              )}
              {canViewVehicleSelection && (
                <FormQuerySelector
                  label="Vehicle"
                  dataHook={useAllVehiclesQuery}
                  serverSideQuery
                  required
                  useWildCardsInQuery={false}
                  variables={{
                    first: 20,
                  }}
                  getOptionLabel={item => {
                    if (item.__isNew__)
                      return (
                        <div
                          onClick={e => {
                            e.stopPropagation()
                          }}
                        >
                          <Link
                            className="!w-full"
                            to={`${
                              import.meta.env.VITE_FMS_UI
                            }/d/registration/vehicle/new?redirect=${encodeURIComponent(
                              window.location.href
                            )}`}
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              persistRegistrationFormState(form.values)
                              window.location.href = `${
                                import.meta.env.VITE_FMS_UI
                              }/d/registration/vehicle/new?redirect=${encodeURIComponent(
                                window.location.href
                              )}`
                            }}
                          >
                            {`${item?.label}`}
                          </Link>
                        </div>
                      )
                    return item.name
                  }}
                  {...inputProps("vehicle", {
                    group: SharedEntityGroup.StarlingVehicle,
                  })}
                  disabled={
                    isStatusGenerateSale ||
                    isStatusInvoiced ||
                    (isCompletelyDisabled && !isHishobPatti)
                  }
                  isCreatable
                />
              )}
            </div>
          </FormCard>

          <QCObservations form={qcForm} commodity={form.values.commodity} />

          {isPackaging ? (
            <FormCard title="Trade Particulars">
              <div className="mb-3 border rounded-lg overflow-hidden">
                <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 p-3 md:p-4 bg-gradient-to-r from-green-50 to-transparent border-b">
                  <h6 className="text-base font-semibold text-gray-800">
                    Multiple Packaging
                  </h6>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => {
                        setAttachmentModal(true)
                      }}
                      variant="ghost"
                      size="sm"
                      intent="info"
                      className="flex items-center gap-1"
                      leftIcon={<Paperclip size={15} />}
                    >
                      {form.values.attachments?.length || 0}
                    </Button>
                  </div>
                </header>
                <div className="p-3 md:p-4 space-y-4">
                  {state?.trade_items?.map((item, index) => {
                    let itemRemoved = removedTradeItems[item.id] ? true : false

                    // let remainingQuantity = getRemainingQty(item)

                    // const qcResult = qcForm.values?.results?.find(
                    //   result =>
                    //     result.template.grade.name === item.variety_grade?.name
                    // )

                    // const baseRateKey = qcResult ? "daily_rate" : "rate"
                    // const dailyRate = Number(item.daily_rate) || 0
                    // const evalutation = qcResult?.evaluations?.[0]
                    // const recommendedDeducatedRate =
                    //   dailyRate - dailyRate * ((evalutation?.value || 0) / 100)

                    return (
                      <div
                        key={index}
                        className={classNames(
                          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 rounded-lg border-2 transition-all",
                          itemRemoved
                            ? "bg-gray-50 border-gray-200 opacity-60"
                            : "bg-white border-green-100 hover:border-green-200 hover:shadow-sm"
                        )}
                      >
                        <FormQuerySelector
                          label="Packaging Material Type"
                          serverSideQuery
                          dataHook={useAllPackagingCommoditiesQuery}
                          {...inputProps("commodity", {
                            group: SharedEntityGroup.KiteCommodity,
                          })}
                          className={"w-full"}
                          required
                          // isDisabled={isDisabled || disableCommodity}
                        />

                        <FormQuerySelector
                          label="Packaging Name"
                          dataHook={useAllVarietiesQuery}
                          variables={{
                            commodity_id: form.values.commodity?.id,
                          }}
                          {...inputProps("commodity_variety", {
                            group: SharedEntityGroup.KiteVariety,
                          })}
                          className={"w-full"}
                          required
                          // isDisabled={isDisabled || disableVariety}
                        />
                        <div className="flex flex-col gap-3 w-full">
                          <FormInput
                            label="Rate"
                            {...inputProps(`trade_items.${index}.rate`)}
                            disabled={
                              isStatusGenerateSale ||
                              (isCompletelyDisabled && !isHishobPatti) ||
                              itemRemoved
                            }
                            className="self-end w-full"
                            leftElement={
                              <Tag className="ml-1" variant="minimal">
                                {defaultCurrency?.name || "INR"}
                              </Tag>
                            }
                            required
                          />
                        </div>

                        <FormInput
                          label={"Quantity"}
                          {...inputProps(`trade_items.${index}.quantity`)}
                          disabled={
                            isStatusGenerateSale ||
                            (isCompletelyDisabled && !isHishobPatti) ||
                            itemRemoved
                          }
                          inputProps={{
                            ...inputProps(`trade_items.${index}.quantity`),
                            id: "vt_weight_listener",
                            type: "number",
                            className: isPackaging ? "" : "mr-24",
                            onChange: e => {
                              let value = Number(e.target.value)

                              inputProps(
                                `trade_items.${index}.quantity`
                              ).onChange(value)
                            },
                          }}
                          type="number"
                          className=""
                          required
                          rightElement={
                            <FormQuerySelector
                              components={{
                                IndicatorSeparator: () => null,
                                ClearIndicator: () => null,
                              }}
                              getControlStyles={prev => {
                                return {
                                  ...prev,
                                  border: "none",
                                }
                              }}
                              dataHook={useAllSiUnitsQuery}
                              variables={{ type: ["Weight", "SKU"] }}
                              isDisabled={
                                isStatusGenerateSale ||
                                (isCompletelyDisabled && !isHishobPatti) ||
                                itemRemoved ||
                                index > 0
                              }
                              {...inputProps(`quantity_unit`, {
                                group: SharedEntityGroup.KiteSiUnit,
                              })}
                            />
                          }
                        />

                        <div className="flex gap-4">
                          <div className="flex flex-col gap-3">
                            <FormInput
                              label="Total Amount"
                              className="flex-[0.75] text-right"
                              value={inr(
                                (item.quantity || 0) * (item["rate"] || 0)
                              )}
                              disabled
                            />
                          </div>
                          <div className="flex self-start justify-center">
                            {isBookingTrade ||
                            isStatusGenerateSale ||
                            isParentBookingTrade ? null : !isTradeConfirmed ? (
                              <button
                                className="text-red-500 pt-5"
                                onClick={async () => {
                                  if (item.id) {
                                    return
                                    // await deleteTradeItem({ id: item.id })
                                  }

                                  const stateCopy = cloneDeep(state)
                                  stateCopy.trade_items.splice(index, 1)
                                  stateCopy.trade_items = stateCopy.trade_items
                                    ?.length
                                    ? stateCopy.trade_items
                                    : [{}]
                                  setState(stateCopy)
                                  runComputations(stateCopy)
                                }}
                              >
                                <Trash height={24} width={24} />
                              </button>
                            ) : isHishobPatti ? (
                              <Checkbox
                                key={itemRemoved.toString()}
                                checked={itemRemoved}
                                onChange={() => {
                                  setRemovedTradeItems(prev => {
                                    if (itemRemoved) {
                                      delete prev[item.id]
                                    } else {
                                      prev[item.id] = true
                                    }
                                    return prev
                                  })
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="px-3 md:px-4 pb-3">
                  {(() => {
                    const gst = {
                      ...createEmptyTradeCommodityBlock().gst,
                      ...(state?.trade_commodity_items?.[0]?.gst || {}),
                    }
                    const totalSupplierGst =
                      (gst.supplier_igst || 0) +
                      (gst.supplier_sgst || 0) +
                      (gst.supplier_cgst || 0)
                    const totalBuyerGst =
                      (gst.buyer_igst || 0) +
                      (gst.buyer_sgst || 0) +
                      (gst.buyer_cgst || 0)
                    const setGstField = (
                      field: string,
                      value: any,
                      mirrorTopLevel = false
                    ) => {
                      const stateCopy = cloneDeep(form.values)
                      if (!stateCopy.trade_commodity_items?.length) {
                        stateCopy.trade_commodity_items = [
                          createEmptyTradeCommodityBlock(),
                        ]
                      }
                      stateCopy.trade_commodity_items[0].gst = {
                        ...createEmptyTradeCommodityBlock().gst,
                        ...stateCopy.trade_commodity_items[0].gst,
                        [field]: value,
                      }
                      if (mirrorTopLevel) {
                        stateCopy[field] = value
                      }
                      setState(stateCopy)
                      runComputations(stateCopy)
                    }
                    const gstDisabled =
                      isStatusGenerateSale ||
                      (isCompletelyDisabled && !isHishobPatti)
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <GstBreakdownCard
                          label="Supplier"
                          autoCalculated={gst.supplier_gst_auto_calculated}
                          onAutoChange={val => {
                            setGstField("supplier_gst_auto_calculated", val)
                            setGstField("_gstFromBackend", false)
                          }}
                          isDisabled={gstDisabled}
                          isSameState={(() => {
                            const deptCode =
                              departmentData?.address?.state_region?.code
                            const sellerCode =
                              state?.seller?.current_address?.state_region?.code
                            if (!deptCode || !sellerCode) return undefined
                            return deptCode === sellerCode
                          })()}
                          totalGst={totalSupplierGst}
                          igst={gst.supplier_igst || 0}
                          sgst={gst.supplier_sgst || 0}
                          cgst={gst.supplier_cgst || 0}
                          igstPct={gst.supplier_igst_percentage}
                          sgstPct={gst.supplier_sgst_percentage}
                          cgstPct={gst.supplier_cgst_percentage}
                          igstField="supplier_igst_percentage"
                          sgstField="supplier_sgst_percentage"
                          cgstField="supplier_cgst_percentage"
                          onPctChange={(field, pct) => {
                            setGstField(field, pct)
                          }}
                          attachments={gst.seller_gst_attachments || []}
                          onAddAttachment={(newOnes: GstAttachment[]) => {
                            setGstField(
                              "seller_gst_attachments",
                              [
                                ...(gst.seller_gst_attachments || []),
                                ...newOnes,
                              ],
                              true
                            )
                          }}
                          onRemoveAttachment={(index: number) => {
                            const arr = [...(gst.seller_gst_attachments || [])]
                            arr.splice(index, 1)
                            setGstField("seller_gst_attachments", arr, true)
                          }}
                        />
                        <GstBreakdownCard
                          label="Buyer"
                          autoCalculated={gst.buyer_gst_auto_calculated}
                          onAutoChange={val => {
                            setGstField("buyer_gst_auto_calculated", val)
                            setGstField("_gstFromBackend", false)
                          }}
                          isDisabled={gstDisabled}
                          isSameState={(() => {
                            const deptCode =
                              departmentData?.address?.state_region?.code
                            const buyerCode =
                              state?.buyer?.current_address?.state_region?.code
                            if (!deptCode || !buyerCode) return undefined
                            return deptCode === buyerCode
                          })()}
                          totalGst={totalBuyerGst}
                          igst={gst.buyer_igst || 0}
                          sgst={gst.buyer_sgst || 0}
                          cgst={gst.buyer_cgst || 0}
                          igstPct={gst.buyer_igst_percentage}
                          sgstPct={gst.buyer_sgst_percentage}
                          cgstPct={gst.buyer_cgst_percentage}
                          igstField="buyer_igst_percentage"
                          sgstField="buyer_sgst_percentage"
                          cgstField="buyer_cgst_percentage"
                          onPctChange={(field, pct) => {
                            setGstField(field, pct)
                          }}
                          attachments={gst.buyer_gst_attachments || []}
                          onAddAttachment={(newOnes: GstAttachment[]) => {
                            setGstField(
                              "buyer_gst_attachments",
                              [
                                ...(gst.buyer_gst_attachments || []),
                                ...newOnes,
                              ],
                              true
                            )
                          }}
                          onRemoveAttachment={(index: number) => {
                            const arr = [...(gst.buyer_gst_attachments || [])]
                            arr.splice(index, 1)
                            setGstField("buyer_gst_attachments", arr, true)
                          }}
                        />
                      </div>
                    )
                  })()}
                </div>
                <div className="border-t-2 border-green-100 bg-gradient-to-b from-gray-50 to-white">
                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b">
                    <Text className="font-semibold text-gray-700 md:flex-[2_1_0%]">
                      Total
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm text-gray-500 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-medium">
                        {state.total_quantity}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm text-gray-500 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-medium">
                        {inr(state.total_amount)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>

                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 bg-gradient-to-r from-green-100 to-green-50 border-t-2 border-green-200">
                    <Text className="text-lg font-bold text-green-800 md:flex-[2_1_0%]">
                      Final
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-bold text-green-800">
                        {(
                          (form.values.total_quantity || 0) -
                          (form.values.quantity_deducted || 0)
                        )?.toFixed(2)}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-bold text-green-800 text-lg">
                        {inr(finalTotal)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>
                </div>
              </div>
              {/* {!canCreateTrade && creditForm.values?.credit?.id && (
              <InsufficientCreditNote />
            )} */}
            </FormCard>
          ) : (
            <>
              <TradeParticulars
                form={form}
                qcForm={qcForm}
                inputProps={inputProps}
                runComputations={runComputations}
                defaultCurrency={defaultCurrency}
                getRemainingQty={getRemainingQty}
                removedTradeItems={removedTradeItems}
                setRemovedTradeItems={setRemovedTradeItems}
                finalTotal={finalTotal}
                showDeviationInDraftWarning={showDeviationInDraftWarning}
                total_returned={total_returned}
                bookingSummaryId={bookingSummaryId}
                offerId={offerId}
                createSauda={createSauda}
                isStatusGenerateSale={isStatusGenerateSale}
                isStatusInvoiced={isStatusInvoiced}
                isCompletelyDisabled={isCompletelyDisabled}
                isHishobPatti={isHishobPatti}
                isBookingTrade={isBookingTrade}
                isParentBookingTrade={isParentBookingTrade}
                isParentSaudaPavti={isParentSaudaPavti}
                isOrderPatti={isOrderPatti}
                newStoragePatti={newStoragePatti}
                inwardFromDemand={inwardFromDemand}
                newHishobPatti={newHishobPatti}
                offerOrderHasOffers={Boolean(offerOrderData?.offers?.length)}
                isTradeConfirmed={isTradeConfirmed}
                allowMultipleCommodity={Boolean(state.allow_multiple_commodity)}
                supplierSameState={(() => {
                  const deptCode = departmentData?.address?.state_region?.code
                  const sellerCode =
                    state?.seller?.current_address?.state_region?.code
                  if (!deptCode || !sellerCode) return undefined
                  return deptCode === sellerCode
                })()}
                buyerSameState={(() => {
                  const deptCode = departmentData?.address?.state_region?.code
                  const buyerCode =
                    state?.buyer?.current_address?.state_region?.code
                  if (!deptCode || !buyerCode) return undefined
                  return deptCode === buyerCode
                })()}
                deleteSellerGstAttachments={async (vars: any) =>
                  deleteSellerGstAttachments(vars)
                }
                deleteBuyerGstAttachments={async (vars: any) =>
                  deleteBuyerGstAttachments(vars)
                }
                disableAttachments={
                  canAddAttachmentsAfterTradeVerified
                    ? false
                    : isStatusGenerateSale ||
                      (isCompletelyDisabled && !isHishobPatti) ||
                      isStatusInvoiced
                }
                disableAddCommodityGrade={
                  isCompletelyDisabled ||
                  // isBookingTrade ||
                  isParentBookingTrade ||
                  isOrderPatti ||
                  createSauda ||
                  newStoragePatti ||
                  inwardFromDemand ||
                  isParentSaudaPavti
                }
              />
              {deviation &&
              dData?.trade_by_pk?.total_amount !== state?.total_amount &&
              state.buyer ? (
                <div className="mt-2">
                  <Toast
                    className="!min-w-full"
                    title="Trade amount exceeding Credit Limit!"
                    intent="warning"
                  />
                </div>
              ) : null}
            </>
          )}
          <TradeExpenses
            form={form}
            inputProps={inputProps}
            isDisabled={isCompletelyDisabled}
            sharedDeptId={sharedDeptId}
            advancePaymentDisabled={!canAddAdvancePayment(state.kind)}
            showOverallExpenseGst={true}
            expenseGstManualOnly={expenseGstManualOnly}
            supplierSameState={(() => {
              const deptCode = departmentData?.address?.state_region?.code
              const sellerCode =
                state?.seller?.current_address?.state_region?.code
              if (!deptCode || !sellerCode) return undefined
              return deptCode === sellerCode
            })()}
            buyerSameState={(() => {
              const deptCode = departmentData?.address?.state_region?.code
              const buyerCode =
                state?.buyer?.current_address?.state_region?.code
              if (!deptCode || !buyerCode) return undefined
              return deptCode === buyerCode
            })()}
            onExpenseGstFieldChange={(field, value) => {
              const updatedValues = { ...form.values, [field]: value }
              setTimeout(() => runComputations(updatedValues), 0)
            }}
            onRemoveExpenseSellerGstAttachment={async index => {
              const arr = [
                ...(form.values.expense_seller_gst_attachments || []),
              ]
              const item = arr[index]
              if (item?.id) {
                await deleteSellerGstAttachments({
                  media_id: [item.id],
                  id: item.media?.id,
                })
                arr.splice(index, 1)
              } else {
                arr.splice(index, 1)
              }
              const updatedValues = {
                ...form.values,
                expense_seller_gst_attachments: arr,
              }
              form.setFieldValue("expense_seller_gst_attachments", arr)
              setTimeout(() => runComputations(updatedValues), 0)
            }}
            onRemoveExpenseBuyerGstAttachment={async index => {
              const arr = [...(form.values.expense_buyer_gst_attachments || [])]
              const item = arr[index]
              if (item?.id) {
                await deleteBuyerGstAttachments({
                  media_id: [item.id],
                  id: item.media?.id,
                })
                arr.splice(index, 1)
              } else {
                arr.splice(index, 1)
              }
              const updatedValues = {
                ...form.values,
                expense_buyer_gst_attachments: arr,
              }
              form.setFieldValue("expense_buyer_gst_attachments", arr)
              setTimeout(() => runComputations(updatedValues), 0)
            }}
            isStatusGenerateSale={isStatusGenerateSale}
            hasInvoice={hasInvoice}
            onExpenseTemplateChange={expenseConfig => {
              const stateCopy = cloneDeep(form.values)
              for (const expense of stateCopy.expenses) {
                setRemovedExpenseItems(prev => {
                  if (expense?.buyer?.id) {
                    prev[expense.buyer.id] = true
                  }
                  if (expense?.seller?.id) {
                    prev[expense.seller.id] = true
                  }
                  return prev
                })
              }
              stateCopy.expenses = normalizeExpenses(
                expenseConfig.expense_configs || [],
                true
              )
              runComputations(stateCopy)
              form.setValues(stateCopy)
            }}
            onDeleteExpense={(idx, expense) => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.expenses.splice(idx, 1)

              setRemovedExpenseItems(prev => {
                if (expense?.buyer?.id) {
                  prev[expense.buyer.id] = true
                }
                if (expense?.seller?.id) {
                  prev[expense.seller.id] = true
                }
                if (expense?.facilitator?.id) {
                  prev[expense.facilitator.id] = true
                }
                return prev
              })
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
            onDeleteAdvancePayment={idx => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.advance_payment.splice(idx, 1)
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
          />

          <>
            {(state?.kind === TradeKinds.HishobPatti ||
              state?.kind === TradeKinds.InwardPatti) &&
              canViewTDS && (
                <TDSComponent
                  disabled={isCompletelyDisabled}
                  checked={form.values.supplier_tds_value}
                  setChecked={checked => {
                    const updatedValues = {
                      ...form.values,
                      supplier_tds_value: checked,
                    }
                    form.setFieldValue("supplier_tds_value", checked)
                    setTimeout(() => runComputations(updatedValues), 0)
                  }}
                  departmentConfig={
                    deptData?.setu_trade_shared_entity?.[0]?.department_config
                  }
                />
              )}

            {(() => {
              const hasOrgConfig =
                (form.values.commodity_variety
                  ?.organisation_commodity_variety_configs?.[0]?.taxes
                  ?.length ?? 0) > 0
              if (!hasOrgConfig) return null

              const sellerMissingState =
                state?.seller?.id &&
                !state?.seller?.current_address?.state_region
              const buyerMissingState =
                state?.buyer?.id && !state?.buyer?.current_address?.state_region

              if (!sellerMissingState && !buyerMissingState) return null

              return (
                <div className="mb-4 flex flex-col gap-2">
                  {sellerMissingState && (
                    <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-md text-sm flex items-center gap-2 font-500">
                      <span>⚠</span> Supplier is missing a state region code.
                      GST cannot be calculated.
                    </div>
                  )}
                  {buyerMissingState && (
                    <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-md text-sm flex items-center gap-2 font-500">
                      <span>⚠</span> Buyer is missing a state region code. GST
                      cannot be calculated.
                    </div>
                  )}
                </div>
              )
            })()}

            <Payment
              finalTotal={finalTotal}
              supplierExpense={form.values.supplier_expense}
              sellerTdsAmount={sellerTdsAmount}
              supplierCharge={supplierCharges}
              buyerExpense={form.values.buyer_expense}
              supplierSgst={state?.supplier_sgst}
              supplierCgst={state?.supplier_cgst}
              supplierIgst={state?.supplier_igst}
              buyerSgst={state?.buyer_sgst}
              buyerCgst={state?.buyer_cgst}
              buyerIgst={state?.buyer_igst}
              expenseSupplierSgst={state?.expense_supplier_sgst}
              expenseSupplierCgst={state?.expense_supplier_cgst}
              expenseSupplierIgst={state?.expense_supplier_igst}
              expenseBuyerSgst={state?.expense_buyer_sgst}
              expenseBuyerCgst={state?.expense_buyer_cgst}
              expenseBuyerIgst={state?.expense_buyer_igst}
              sellerPayable={dData?.trade_by_pk?.seller_payable ?? 0}
              buyerPayable={dData?.trade_by_pk?.buyer_payable ?? 0}
              tradeAdvanceUtilized={
                newHishobPatti ||
                newInwardPatti ||
                newStoragePatti ||
                newBookingPatti
                  ? 0
                  : dData?.trade_by_pk?.seller_bifurcation?.advance_amount
                      ?.val ?? 0
              }
              advanceDetails={
                newHishobPatti ||
                newInwardPatti ||
                newStoragePatti ||
                newBookingPatti
                  ? 0
                  : dData?.trade_by_pk?.settled_by_advance_payment
              }
              isDisabled={isCompletelyDisabled}
            />
          </>

          <TermsAndConditions
            isDisabled={
              isStatusGenerateSale || isCompletelyDisabled || isStatusInvoiced
            }
            kind={form.values.kind}
            mode={form.values.mode}
            sharedDeptId={sharedDeptId}
            seller_tnc={form.values.seller_tnc}
            buyer_tnc={form.values.buyer_tnc}
            onChange={(path, value) => form.setFieldValue(path, value)}
          />
        </div>
      </Group>
      <footer className="fixed bg-white left-0 bottom-0 w-full px-3 py-2 shadow-sm flex flex-wrap justify-end gap-2 z-10">
        {moreTrades &&
        isCompletelyDisabled &&
        state.kind !== "final" &&
        !newInwardPatti &&
        !isBookingTrade &&
        !newStoragePatti &&
        !newHishobPatti &&
        state?.status !== Codenames.DirectSale &&
        !inwardFromDemand &&
        id !== "new" &&
        !hasReceipt ? (
          <>
            <Button
              onClick={() => setSearchParams({ new: "true" })}
              className="!bg-cyan-400"
              leftIcon={<CheckCircle />}
            >
              Generate Hishob Patti
            </Button>
          </>
        ) : null}
        {isCompletelyDisabled ? (
          // &&
          // !(
          //   // isStatusGenerateSale && comment out for the remove save button on Generate sale status.
          //   !hasInvoice &&
          //   !newInwardPatti &&
          //   !isStatusInvoiced
          // )
          !newInwardPatti &&
          state?.status !== Codenames.DirectSale &&
          !isBookingTrade &&
          !newStoragePatti &&
          !newHishobPatti &&
          !inwardFromDemand &&
          !isInvoiceCancelled &&
          id !== "new" ? (
            <Button
              hidden={!canAddAttachmentsAfterTradeVerified}
              loading={updateTradeFetching || mutationFetching}
              onClick={async () => {
                const { data: deptData } = await upsertSharedEntity({
                  group_name: SharedEntityGroup.KiteDepartment,
                  name: currentDept?.name,
                  reference_id: currentDept?.id,
                })
                const { data: iData, error } = await upsertTrade({
                  input: {
                    id:
                      id === "new" ||
                      id === "newInward" ||
                      isHishobPatti ||
                      (newHishobPatti && isStatusGenerateInward) ||
                      (newHishobPatti && isStatusInwardConfirmed) ||
                      isInwardPatti ||
                      newBookingPatti ||
                      newStoragePatti
                        ? undefined
                        : state?.id,
                    attachments: state?.attachments?.map(
                      attachment => attachment.media.id
                    ),
                    ...(!form.values.supplier_tds_value && {
                      seller_tds_percentage: null,
                    }),
                    items: (() => {
                      const blocks = state?.trade_commodity_items?.length
                        ? state.trade_commodity_items
                        : buildTradeCommodityItemsFromState(state)
                      const itemInputs = buildTradeItemInputsFromBlocks(
                        blocks,
                        {
                          defaultCurrency,
                          qcResults: qcForm.values?.results,
                          removedTradeItems,
                          includeId: !(
                            isHishobPatti ||
                            isInwardPatti ||
                            newBookingPatti ||
                            newStoragePatti ||
                            (newHishobPatti && isStatusGenerateInward) ||
                            (newHishobPatti && isStatusInwardConfirmed)
                          ),
                          parentTradeItemIdFor:
                            newHishobPatti ||
                            newBookingPatti ||
                            newInwardPatti ||
                            newStoragePatti
                              ? (grade: any) => grade.id
                              : undefined,
                        }
                      )
                      return itemInputs.length ? itemInputs : undefined
                    })(),
                    packaging: state?.packaging,
                    kind: isHishobPatti ? TradeKinds.HishobPatti : state?.kind,
                    mode: state?.mode,
                    department_id:
                      deptData?.insert_setu_trade_shared_entity_one?.id,
                    // commodity_id: state?.commodity?.shared_id,
                    // trade_amounts_counter: 0,
                  },
                })

                if (error || !iData) {
                  return toast.custom(
                    <Toast
                      title={"Unable to create the trade request"}
                      intent="danger"
                    />
                  )
                }

                if (iData?.add_update_trade?.trade?.id) {
                  await incrementTradeCounter({
                    id: iData.add_update_trade.trade?.id,
                  })

                  toast.custom(
                    <Toast title={"Trade Attachments saved successfully!"} />
                  )
                  navigate(AppRoutes.tradeRequests)
                }
              }}
              leftIcon={<CheckCircle />}
            >
              {"Save "}
              {state?.kind === TradeKinds.HishobPatti || isHishobPatti
                ? "Hishob"
                : state?.kind === TradeKinds.SaudaPavti
                ? "Sauda"
                : state?.kind === TradeKinds.Booking
                ? "Transit"
                : state?.kind === TradeKinds.StoragePatti
                ? "Storage"
                : state?.kind === TradeKinds.OrderPatti
                ? "Order"
                : state?.kind === TradeKinds.InwardPatti
                ? "Inward"
                : null}
              {" Attachments"}
            </Button>
          ) : null
        ) : (
          <Button
            loading={mutationFetching || updateTradeFetching}
            disabled={useCredit && noCreditAvailableHpBp}
            onClick={async () => {
              if (canCreateNewOverdueApproval) {
                setOverdue(true)
              } else if (deviation) {
                setDeviation(true)
              } else {
                await onSubmit({})
              }
            }}
            leftIcon={<CheckCircle />}
          >
            {state?.id ? "Save" : "Create"}{" "}
            {state?.kind === TradeKinds.HishobPatti || isHishobPatti
              ? "Hishob Patti"
              : state?.kind === TradeKinds.SaudaPavti
              ? "Sauda Pavti"
              : state?.kind === TradeKinds.Booking
              ? "Transit Patti"
              : state?.kind === TradeKinds.StoragePatti
              ? "Storage Patti"
              : state?.kind === TradeKinds.OrderPatti
              ? "Order Patti"
              : state?.kind === TradeKinds.InwardPatti
              ? "Inward Patti"
              : null}
          </Button>
        )}
      </footer>
      {id && id !== "new" && (
        <SendConsent
          kind={state.kind}
          buyer={state.buyer}
          seller={state.seller}
          tradeId={id}
          isOpen={isOpen}
          showGenerateSaleAction={
            (state?.parent_trade?.kind === "inward" &&
              state?.kind === "final") ||
            (state?.parent_trade?.kind === "storage" && state?.kind === "final")
          }
          setPackingOpen={() => {
            setTimeout(() => {
              if (pData?.packaging_order.length || 0 > 0) {
                setPackingOpen(true)
                setNavigate(true)
              } else {
                navigate(AppRoutes.tradeRequests)
              }
            }, 500)
          }}
          onClose={() => setOpen(false)}
        />
      )}

      <PackingMaterialSaveModal
        pData={pData}
        isOpen={packingOpen}
        onClose={() => {
          setPackingOpen(false)
          if (isNavigateOn) navigate(AppRoutes.tradeRequests)
        }}
      />
      <Modal
        title="Expense GST"
        isOpen={showExpenseGstManualNotice}
        onClose={() => setShowExpenseGstManualNotice(false)}
        primaryActionButtonProps={{
          onClick: () => setShowExpenseGstManualNotice(false),
          text: "Proceed",
        }}
      >
        <Text>
          Multiple commodity varieties with different GST rates are selected.
          Expense GST cannot be calculated automatically. Please enter the
          applicable GST percentages manually.
        </Text>
      </Modal>
      {attachmentModal && (
        <AttachmentModal
          form={form}
          isOpen={attachmentModal}
          attachments={form.values?.attachments || []}
          addAttachment={attachment => {
            form.setFieldValue("attachments", attachment)
          }}
          disabled={
            canAddAttachmentsAfterTradeVerified
              ? false
              : isStatusGenerateSale ||
                (isCompletelyDisabled && !isHishobPatti) ||
                isStatusInvoiced
          }
          onClose={() => {
            setAttachmentModal(false)
          }}
        />
      )}
      <DeviationModalConfirmation
        isOpen={isDeviation}
        setOpen={setDeviation}
        onConfirm={async () => {
          await onSubmit({ createDeviation: true })
          setDeviation(false)
        }}
        onCancel={async () => {
          setDeviation(false)
        }}
        data={{
          availableAmount:
            creditData?.buyer_credit_wallet_info?.dynamic_available || 0,
          buyerName: state?.buyer?.name,
          tradeAmount:
            finalTotal +
            (state?.buyer_expense || 0) +
            (state?.total_expense_buyer_gst || 0) +
            (state?.buyer_igst || 0) +
            (state?.buyer_sgst || 0) +
            (state?.buyer_cgst || 0) +
            (state?.expense_buyer_igst || 0) +
            (state?.expense_buyer_sgst || 0) +
            (state?.expense_buyer_cgst || 0),
          exceedingAmount:
            state?.buyer_amount -
            (creditData?.buyer_credit_wallet_info?.dynamic_available || 0),
          tradeId: tradeId,
          deviationCount:
            (creditData?.buyer_credit_wallet_info?.unsettled_overdraft_count ||
              0) + 1,
        }}
      />

      <OverdueModalConfirmation
        isOpen={isOverdue}
        setOpen={setOverdue}
        onConfirm={async () => {
          await onSubmit({ createOverdue: true })
          setOverdue(false)
        }}
        onCancel={async () => {
          setOverdue(false)
        }}
        data={{
          availableAmount:
            creditData?.buyer_credit_wallet_info?.dynamic_available || 0,
          buyerName: state?.buyer?.name,
          overdueAmount:
            creditData?.buyer_credit_wallet_info?.overdue_amount || 0,
          overdueCount:
            (creditData?.buyer_credit_wallet_info
              ?.last_overdue_approval_count || 0) + 1,
          tradeAmount:
            finalTotal +
            (state?.buyer_expense || 0) +
            (state?.total_expense_buyer_gst || 0) +
            (state?.buyer_igst || 0) +
            (state?.buyer_sgst || 0) +
            (state?.buyer_cgst || 0) +
            (state?.expense_buyer_igst || 0) +
            (state?.expense_buyer_sgst || 0) +
            (state?.expense_buyer_cgst || 0),
          tradeId: tradeId,
        }}
      />
    </>
  )
}

export default TradeRequestDetail
